// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * WebXR session lifecycle.
 *
 * Orchestrates the Three.js import, renderer creation, XR session
 * request, per-frame loop, and teardown. This is the only module
 * that `main.ts` talks to directly — everything else
 * (scene / hud / interaction) is built and torn down here.
 *
 * Three.js is lazy-imported on the first call to {@link enterVr} —
 * desktop web, Tauri, and mobile browsers (where
 * {@link isImmersiveVrSupported} returns false) never fetch the
 * chunk. Same pattern used for Tauri plugins elsewhere in the
 * codebase.
 *
 * See {@link file://./../../docs/VR_INVESTIGATION_PLAN.md VR_INVESTIGATION_PLAN.md}.
 */

import type { RtPlayingEntry } from './overlayDisplay'
import type { RtColours } from './xrToolsHost'
import type { ColorScaleDisplay } from './colorScaleDisplay'
import { dataToolsAllowed } from './planAccess'
import type * as THREE from 'three'
import { createVrScene, type VrSceneHandle, type VrDatasetTexture } from './vrScene'
import { createVrHud, nextSpeed, HUD_HEIGHT, HUD_WIDTH, type VrHudHandle, type VrHudState, type VrVoiceState } from './vrHud'
import { nextSpinRate, spinRadians } from './vrSpin'
import type { MapLayerImages } from './earthTileLayer'
import {
  createVrSidePanel,
  SIDE_PANEL_HEIGHT,
  SIDE_PANEL_WIDTH,
  type MapLayerTintChoice,
  type VrLayersState,
  type VrSatelliteAction,
  type VrPinsState,
  type VrSidePanelHandle,
  type VrViewState,
} from './vrSidePanel'
import { createVrLegend, type VrLegend, type VrLegendHandle } from './vrLegend'
import { createVrPins, type VrPinMark, type VrPinsHandle } from './vrPins'
import type { SurfaceMode } from './measure/surfaceMask'
import { createVrReadout, formatLatLon, type VrReadoutHandle } from './vrReadout'
import { createXrPhoneChrome, type XrPhoneChromeHandle } from '../ui/xrPhoneChrome'
import { getLocale, t } from '../i18n'
import { createVrGeoMedia, type VrGeoMediaHandle, type VrGeoMediaState } from './vrGeoMedia'
import { createVrWorldPanels, type VrWorldPanelsHandle } from './vrWorldPanels'
import { createVrVectorLayers, pxRadAt, type VrVectorLayersHandle } from './vrVectorLayers'
import {
  clearFocusZoom,
  commitFocusZoom,
  dirToLatLon,
  displayToSource,
  getFocusZoom,
  isZoomed,
  resetZoom,
  setFocusZoom,
  plainZoom,
  type FocusZoomState,
  type Vec3,
} from './vrFocusZoom'
import { createVrFocusZoomView } from './vrFocusZoomView'
import { isFineGrid } from './globeZoomCeiling'
import { createVrSatellites, type VrSatellitesHandle } from './vrSatellites'
import { createVrSatelliteSky, type VrSatelliteSkyHandle } from './vrSatelliteSky'
import type { Observer } from './satellites/skyMath'
import { createVrNightSky, skyTurnForGlobe } from './vrNightSky'
import type { SatelliteLayer } from './satellites/satelliteLayer'
import { satelliteFactsText } from './satellites/satelliteText'
import type { VectorFrameLayer } from './vector/vectorPick'
import { createVrKhipuPanel, KHIPU_PANEL_HEIGHT, KHIPU_PANEL_WIDTH, type VrKhipuAction, type VrKhipuPanelHandle, type VrKhipuState } from './vrKhipuPanel'
import { createVrTimeLabel, type VrTimeLabelHandle } from './vrTimeLabel'
import { createVrTimelineTrack, type VrTimelineTrackHandle } from './vrTimelineTrack'
import { formatPlayheadLabel, type TimelineAvailabilitySpan } from './timelineTrackCanvas'
import { createVrInteraction, type VrInteractionHandle } from './vrInteraction'
import { createVrLoading, type VrLoadingHandle } from './vrLoading'
import {
  formatProbeReading,
  probeDatasetValue,
  sphereUvToLatLon,
  type ProbeSource,
} from './datasetProbe'
import { getSharedLumaSampler } from './glLumaSampler'
import { createVrZoomOverlay, type VrZoomOverlayHandle } from '../ui/vrZoomOverlay'
import { createVrPlacementTouch, type VrPlacementTouchHandle } from '../ui/vrPlacementTouch'
import { createVrRotateTouch, type VrRotateTouchHandle } from '../ui/vrRotateTouch'
import { tiltGlobe } from './vrGlobeTilt'
import { createVrMarkerAlign, isMarkerAlignRequested } from './vrMarkerAlign'
import { createVrMarkerOutline } from './vrMarkerOutline'
import type { MarkerFrame } from './sharedMarkerPose'
import type { RoomGlobe } from './roomProtocol'
import { createVrMarkerAlignTouch, type VrMarkerAlignTouchHandle } from '../ui/vrMarkerAlignTouch'
import { createVrDebugPanel, type VrDebugPanelHandle } from './vrDebugPanel'
import { FOLLOWER_MAX_GLOBE_SCALE, GLOBE_RADIUS, MAX_GLOBE_SCALE, MIN_GLOBE_SCALE } from './vrScene'
import { createVrPlacement, type VrPlacementHandle } from './vrPlacement'
import { computeGazeSpawnPosition } from './vrSpawn'
import {
  MARKER_GAP_M,
  MARKER_GLOBE_SCALE,
  SURFACE_GAP_M,
  decodeSavedPlacement,
  lowestCentreAbove,
  type GlobeHold,
} from './vrGlobeHold'
import { createGlobePlacement } from './vrGlobePlacement'
import { joinedRoomCode } from './roomClient'
import {
  clearPersistedAnchorHandle,
  loadPersistedAnchorHandle,
  savePersistedAnchorHandle,
} from '../utils/vrPersistence'
import { getBordersVisible } from '../utils/viewPreferences'
import { logger } from '../utils/logger'
import { emit, emitCameraSettled } from '../analytics'
import type { VrExitReason } from '../types'
import {
  classifyXrDevice,
  getInputArchetype,
  isHandheldArSession,
  isHandheldArUserAgent,
  type VrInputArchetype,
} from '../utils/vrCapability'

/**
 * Contract the hosting app must provide. Pull-based: the session
 * polls these getters once per frame and reflects the values in the
 * HUD / scene. Keeps the VR modules decoupled from the specifics of
 * MapLibre / HLSService / viewportManager.
 */
/** 1x1 scratch canvas for the in-VR readout. Module-scoped and reused
 *  so a per-frame probe allocates nothing. */

/**
 * How often the VR probe actually samples, in ms.
 *
 * The readout is a number a person reads off a HUD; 10 Hz is already
 * faster than anyone can follow it changing. What it replaces is not
 * free: a raycast plus a `drawImage` and a **synchronous**
 * `getImageData`, on a loop that has 11-14 ms to render two eyes. The
 * 1x1 copy keeps the transfer small but not the readback stall, and
 * that stall would otherwise land 72-90 times a second.
 */
const VR_PROBE_INTERVAL_MS = 100
/**
 * AYNI (Quest review D2, 2026-11-03): while a video plays, at most four a
 * second. Each sample of a new frame copies the whole frame into the
 * sampler's own context (32 MiB at 4K) before its one-texel read, so a
 * playing video paid that ten times a second; a paused one copies nothing
 * new and keeps the faster cadence that follows the ray.
 */
const VR_PROBE_PLAYING_INTERVAL_MS = 250

/** AYNI (Quest review D10): the frame rate asked of a headset that offers it. */
const TARGET_FRAME_RATE = 72

/** -1, not 0: the XR clock legitimately reads 0 on the first frame. */
let vrProbeLastAt = -1
let vrProbeLastValue: string | null = null

/**
 * AYNI (Quest review D7, 2026-11-03): free a session's renderer and its
 * WebGL context at once. A new renderer is made on every entry, and a
 * context left for the garbage collector stays live; browsers cap live
 * contexts (about 16) and drop the oldest past that, which can be the
 * browser globe's own.
 */
function releaseRenderer(r: THREE.WebGLRenderer): void {
  r.dispose()
  r.forceContextLoss()
}

/** Forget the cached reading so a new session starts clean. */
function resetVrProbe(): void {
  vrProbeLastAt = -1
  vrProbeLastValue = null
}

/**
 * Value under the controller's aim, for the HUD — sampled at most
 * every `VR_PROBE_INTERVAL_MS`, returning the previous reading in
 * between.
 *
 * Held to a fixed cadence rather than driven by the HUD's own
 * change-detection, because the HUD debounces the *redraw* and this
 * is the cost of producing the value it debounces on.
 */
function readVrProbe(
  interaction: VrInteractionHandle,
  ctx: VrSessionContext,
  now: number,
): string | null {
  const source = ctx.getDatasetTexture()?.element
  const playing = typeof HTMLVideoElement !== 'undefined' && source instanceof HTMLVideoElement && !source.paused
  const interval = playing ? VR_PROBE_PLAYING_INTERVAL_MS : VR_PROBE_INTERVAL_MS
  if (vrProbeLastAt >= 0 && now - vrProbeLastAt < interval) {
    return vrProbeLastValue
  }
  vrProbeLastAt = now
  vrProbeLastValue = sampleVrProbe(interaction, ctx)
  return vrProbeLastValue
}

/**
 * One probe sample.
 *
 * Returns null — and the HUD drops the line entirely — for a picture
 * dataset, a controller not aimed at a globe, a point outside a
 * regional dataset's box, or a frame that hasn't decoded. So every
 * dataset published before this feature keeps exactly the HUD it has
 * today.
 *
 * Copies a single texel rather than a frame: at 4096x2048 a full read
 * would be 32 MB.
 */
function sampleVrProbe(
  interaction: VrInteractionHandle,
  ctx: VrSessionContext,
): string | null {
  // AYNI: values are part of the plans with the data tools (planAccess.ts).
  if (!dataToolsAllowed()) return null
  // AYNI (owner, 2026-10-12): a dataset hidden with its eye is not read.
  if (ctx.getDatasetLook?.().hidden) return null
  const spec = ctx.getDatasetTexture()
  if (!spec?.options?.colorScale) return null
  const uv = interaction.globeHoverUv()
  if (!uv) return null
  const sampler = getSharedLumaSampler()
  if (!sampler) return null
  const source = spec.element
  // ImageBitmap is a valid THREE texture source but not one the
  // sampler's texImage2D overload accepts; skip rather than cast.
  if (typeof ImageBitmap !== 'undefined' && source instanceof ImageBitmap) return null
  const { lat, lon } = sphereUvToLatLon(uv)
  const reading = probeDatasetValue(
    lat,
    lon,
    source as ProbeSource,
    (src, texel) => sampler.sample(src, texel),
    spec.options,
  )
  return reading ? formatProbeReading(reading) : null
}

/**
 * The primary dataset's declared time axis, in the shape the date track
 * draws. The host answers it from the `.dsa` beside the stream
 * (`services/dsaTimeline.ts`) plus the video's own playhead; a dataset
 * that declares no axis — every catalog row, and any stream whose
 * annotation has not arrived — answers `null`, and the track hides.
 */
export interface VrDatasetTimeline {
  /** Instant of the first frame, ms since epoch. */
  readonly startMs: number
  /** Exclusive end of the axis — the instant after the final frame. */
  readonly endMs: number
  /** The instant the playhead represents now. */
  readonly currentMs: number
  readonly frameCount: number
  readonly cadenceMs: number
  /** Sparse provenance spans for the track to shade; a frame outside them is data.
   *  The renderer's own span shape — three fields it draws — rather than the
   *  DSA's full record, so a second reader of the annotation cannot make the
   *  drawing depend on a field it never paints. */
  readonly availabilitySpans: readonly TimelineAvailabilitySpan[]
}

export interface VrSessionContext {
  /**
   * The currently-loaded dataset's surface texture for the PRIMARY
   * panel. Backward-compatible convenience that equals
   * `getPanelTexture(getPrimaryIndex())`. Kept for single-globe
   * callers that don't care about the multi-panel model.
   */
  getDatasetTexture(): VrDatasetTexture | null
  /** Dataset title for the HUD (primary panel). null/empty → "No dataset loaded". */
  getDatasetTitle(): string | null
  /** Dataset id for the primary panel — used by analytics events
   * fired from inside VR (e.g. `vr_placement.layer_id`). Null when
   * no dataset is loaded. Telemetry-only: the HUD itself uses
   * `getDatasetTitle()` for human display. */
  getDatasetId(): string | null
  /**
   * Formatted time-label string for the current primary dataset —
   * mirrors the 2D `#time-label` overlay (e.g. `"2023-06-15"` or
   * `"2023-06-15 18:00"` for sub-daily). null when the dataset
   * lacks `startTime` metadata or no dataset is loaded.
   *
   * Polled per XR frame. The host MUST derive the string from the
   * current playback state (for video datasets, compute from
   * `video.currentTime`) rather than reusing a cached
   * `appState.timeLabel` — WebXR pauses `window.requestAnimationFrame`
   * for the duration of an immersive session, so the 2D
   * `startPlaybackLoop` that normally updates `appState.timeLabel`
   * is frozen and any consumer reading that cache sees the value
   * from the instant VR was entered and stays there forever. See
   * `main.ts`'s implementation for the expected pattern.
   */
  getDatasetTimeLabel(): string | null
  /**
   * The primary dataset's declared time axis, or null when it declares
   * none. Polled per XR frame like the label, and answered the same way:
   * the host reads the stream's `.dsa` (fetched once, cached, and null
   * when absent) and maps the video's `currentTime` through it. A null
   * answer is the normal case rather than a failure — every catalog
   * dataset answers null here, and shows no track.
   */
  getDatasetTimeline(): VrDatasetTimeline | null
  /**
   * Seek the primary playback to an instant on that axis. The host
   * converts through the same mapping, which lands the video **inside**
   * the frame representing that instant rather than on its boundary, so
   * a decode cannot resolve to the neighbouring date. No-op without an
   * axis or a video element.
   */
  seekToTimelineDate(epochMs: number): void
  /** True iff a video dataset is loaded on the primary — drives the HUD play/pause button visibility. */
  hasVideoDataset(): boolean
  /** AYNI (Quest review B2): the primary video is waiting for data while it should play. */
  isBuffering?(): boolean
  /** Drives the HUD play/pause icon. Reflects the primary panel's state. */
  isPlaying(): boolean
  /** Called when the user taps play/pause in VR. Toggles the primary's video. */
  togglePlayPause(): void
  /** Drives the HUD mute icon variant (speaker vs speaker-slash). */
  isMuted(): boolean
  /** Called when the user taps the HUD mute button. Flips `video.muted`. */
  toggleMute(): void

  // --- Phase 2.5 multi-panel getters ---
  //
  // These let vrSession mirror the 2D app's viewport manager
  // inside VR. When the 2D app is in 2-globe layout, `getPanelCount`
  // returns 2, each panel has its own texture / title, and one
  // slot is designated primary (drives the HUD + playback
  // transport). The original Phase 2.5 plan also included a
  // `promotePanel` hook for tap-to-promote (trigger on a secondary
  // globe → that slot becomes primary), but the behaviour was
  // intentionally removed in favour of "grab any globe to rotate,
  // all spin in lockstep" — the original created a ping-pong loop
  // where promoting swapped textures underneath the user's ray,
  // which then promoted again on the next tap. A replacement UX
  // (long-press, HUD-dot taps, Phase 3 browse panel routing) is
  // future work; the context surface stays trimmed until then.

  /** Current number of globe panels (1/2/4). Sourced from the 2D viewport manager. */
  getPanelCount(): number
  /** Which slot is currently primary — drives the HUD + singular playback transport. */
  getPrimaryIndex(): number
  /** Dataset texture for a specific slot, or null if no dataset loaded in that slot. */
  getPanelTexture(slot: number): VrDatasetTexture | null
  /** Dataset title for a specific slot, for per-panel labels; null if no dataset. */
  getPanelTitle(slot: number): string | null

  // --- Phase 3: in-VR browse ---
  /** Full dataset catalog for the browse panel. */
  getDatasets(): VrDatasetEntry[]
  /** Load a dataset by ID without leaving VR. */
  loadDataset(id: string): void

  // --- The assistant's voice in the headset (HUD mic + caption strip) ---
  /**
   * The assistant's voice turn for the HUD mic + caption strip, polled per
   * XR frame. Null, or the member absent, hides the mic. AYNI: nothing
   * wires it since terraviz's assistant and its voice stack were removed
   * (2026-10-03); the seam is kept for the AYNI chatbot's VR/AR voice.
   */
  getVoiceState?(): VrVoiceState | null
  /** The HUD mic was tapped: start listening, send, or stop speaking, depending on the turn. */
  toggleVoice?(): void

  // --- AYNI: layer stack ---
  /**
   * The basemap + overlays for the primary globe (mapLayers.ts), or null.
   * Polled per XR frame and applied only when the object changes, so the
   * host hands back the same object until the stack actually changes.
   */
  getMapLayerImages?(): MapLayerImages | null
  /** AYNI (owner, 2026-10-12): the primary dataset's opacity (1 opaque) and eye, as the viewer set them. */
  getDatasetLook?(): { opacity: number; hidden: boolean }

  // --- AYNI: geo-media (radio stations, wildlife cams) ---
  /**
   * The loaded geo-media dataset — its markers and what its player
   * shows — or null. Polled per XR frame and applied on change, so the
   * host hands back the same object until something changed.
   */
  getGeoMedia?(): VrGeoMediaState | null

  // --- AYNI: DSA 1.8 vector layers ---
  /**
   * Each globe's vector layers as the browser's player drew them this
   * frame, by panel slot (null: none). Asked every XR frame; it also ticks
   * the players, whose own animation frames stop in an immersive session.
   */
  getVectorFrames?(): Array<{ version: number; layers: readonly VectorFrameLayer[] } | null>
  /** The vector feature a tap on the globe inspected (its panel beside the globe), or null. */
  getVectorInspection?(): { key: string; text: string } | null
  /**
   * AYNI (satellites plan): the page's Satellites layer. The session ticks
   * its clock each XR frame (the page's own loop stops in a session) and
   * draws it as the orbit shell around the primary globe.
   */
  getSatellites?(): SatelliteLayer | null

  // --- AYNI: Khipus (Khipu plan, slice 4) ---
  /**
   * The Khipu or lesson playing in its player, for the headset's Khipu
   * panel, or null. Polled per XR frame and drawn on change, so the host
   * hands back the same object until something changed.
   */
  getKhipu?(): VrKhipuState | null
  /** A press on the Khipu panel: Previous, Play/Pause, Next (Finish on the last), Close. */
  khipuAction?(action: VrKhipuAction): void
  /** A tap on a globe at this place: a trigger or a phone's tap released without turning it. */
  /**
   * A tap on a globe at this place: a trigger or a phone's tap released
   * without turning it. AYNI: `kind` says what may come of it — a headset's
   * trigger never drops a pin ('select'), a phone's tap does in pin mode
   * ('touch'), and A / X always asks for one ('pin').
   */
  onGlobeTap?(lat: number, lon: number, kind?: VrGlobeTapKind): void

  // --- AYNI: the AR refresh (2026-10-04) ---
  /**
   * What the dock, the side panel, the legend, the pins and a phone's
   * overlay controls read and change. Optional so a host without these
   * tools still gets a working session (the panels then say there is
   * nothing to show).
   */
  tools?: VrToolsHost

  /**
   * Optional — fired after the session ends + resources are torn down.
   * AYNI: with the place the globe last faced the viewer with, so the
   * browser globe can turn to it.
   */
  onSessionEnd?: (lastView?: { lat: number; lon: number }) => void
  /** AYNI (Quest review A5): fired once the session is live (the Enter button, vrButton.ts). */
  onSessionStart?: () => void
}

/**
 * AYNI: the app's tools as an immersive session uses them (the AR
 * refresh, 2026-10-04). main.ts implements it over the same state the
 * browser's panels use, so a change made in the headset or over a phone's
 * camera is there in the browser afterwards, and the other way round.
 */
export interface VrToolsHost {
  stepFrame(direction: -1 | 1): void
  playbackRate(): number
  setPlaybackRate(rate: number): void
  /** Where a video is, 0..1, or null when none plays. */
  playbackProgress(): number | null
  /** Move a video to a fraction of its length. */
  seekProgress(fraction: number): void
  /** The phone track's two end labels: the axis's dates, or the playing time. */
  trackLabels(): [string, string] | null
  /** A meeting's presenter has the controls. */
  isLocked(): boolean
  /** Whether the dataset is a live stream (the phone's top line says LIVE). */
  isLive(): boolean
  /** The legend, for the headset's legend panel. */
  legend(): VrLegend | null
  /** A change key for the legend, and a fresh DOM legend for a phone. */
  legendKey(): string
  legendElement(): HTMLElement | null
  /** AYNI (owner, 2026-10-11): the legend is parked on the View page (Tools → View) rather than beside the globe. */
  legendParked(): boolean
  setLegendParked(parked: boolean): void
  /** The Data tab's pins, to draw on the globe. */
  pinMarks(): VrPinMark[]
  /** The Data page. */
  pins(): VrPinsState | null
  setPinPicking(on: boolean): void
  removePin(id: string): void
  /** AYNI: give a pin a name, or its own back with an empty one. */
  renamePin(id: string, name: string): void
  clearPins(): void
  markExtremes(): void
  /** AYNI (owner, 2026-10-18): the highest and lowest follow the data or stay; their track shown or not. */
  toggleFollow(): void
  toggleTrack(): void
  setSurface(mode: SurfaceMode): void
  /** Browse: recently opened ids, and a ranked search. */
  recentIds(): string[]
  search(query: string): string[] | null
  /** A stream outside the plan was chosen in the headset. */
  lockedStream(packageName: string, title: string): void
  layers(): VrLayersState | null
  /** AYNI (satellites plan): a press in the Layers page's Satellites section. */
  satellite?(action: VrSatelliteAction): void
  setOverlay(id: string | null): void
  setTint(id: string, tint: MapLayerTintChoice): void
  toggleRt(id: string | null): void
  /** AYNI: the streams playing now, bottom first, and hiding one with its eye. */
  rtPlaying(): ReadonlyArray<RtPlayingEntry>
  setRtHidden(id: string, hidden: boolean): void
  /** AYNI (owner, 2026-10-12): a stream's opacity (set, or a step of the headset's − / +). */
  setRtOpacity(id: string, opacity: number): void
  stepRtOpacity(id: string, dir: -1 | 1): void
  /** AYNI (owner, 2026-10-12): the dataset's name, opacity and eye; its eye, opacity and ✕. */
  baseLook(): { title: string; opacity: number; hidden: boolean } | null
  /** AYNI (owner, 2026-10-13): the dataset's colours for a phone's palette button (scale null: they cannot change), and setting them. */
  baseColours(): RtColours | null
  setBaseDisplay(display: ColorScaleDisplay): void
  setBaseHidden(hidden: boolean): void
  setBaseOpacity(opacity: number): void
  stepBaseOpacity(dir: -1 | 1): void
  closeBase(): void
  /** AYNI (2026-10-09): move one to `to` in the stack (0 is the lowest); its colours. */
  moveRt(id: string, to: number): void
  rtColours(id: string): RtColours | null
  setRtDisplay(id: string, display: ColorScaleDisplay): void
  setRtPalette(id: string, palette: string): void
  stepRtLimit(id: string, end: 'min' | 'max', dir: -1 | 1): void
  setRtInverted(id: string, on: boolean): void
  view(): VrViewState | null
  setPalette(id: string): void
  setInverted(on: boolean): void
  resetView(): void
  /** A phone's Browse sheet: the browser's own panel. */
  openBrowse(): void
  closeBrowse(): void
}

/** AYNI: what a press on the globe may do (see VrSessionContext.onGlobeTap). */
export type VrGlobeTapKind = 'select' | 'touch' | 'pin'

/** AYNI: the live session's notice line, for messages from the app (a refused action). */
let notifyActive: ((message: string) => void) | null = null

/**
 * AYNI: say something to the person in the session — on the dock in a
 * headset, at the foot of the screen on a phone. No-op outside a session.
 */
export function notifyXr(message: string): void {
  notifyActive?.(message)
}

/**
 * Lightweight dataset descriptor for the VR browse panel. Avoids
 * importing the full `Dataset` type into the VR modules — they only
 * need what they render.
 */
export interface VrDatasetEntry {
  id: string
  title: string
  /**
   * Every category/tag this dataset belongs to — union of
   * `enriched.categories` keys and `Dataset.tags`, matching the 2D
   * browse UI's chip-building model. Empty when the dataset has
   * neither. Lets the VR browse panel surface chips like "Real-Time"
   * that live in `tags`, not just in enriched
   * categories.
   */
  categories: string[]
  /** Thumbnail URL from the SOS catalog, if available. */
  thumbnailUrl: string | null
  /** AYNI: what it is (a DataKind: 'real-time', 'forecast', …), its label, and where the kind sorts. */
  kind?: string
  kindLabel?: string
  kindOrder?: number
  /** AYNI: its themes, labelled, with where each sorts. */
  themes?: ReadonlyArray<{ id: string; label: string; order?: number }>
  /** AYNI: the package it needs that the plan lacks (its name), or null. */
  locked?: string | null
}

/**
 * Lazy Three.js loader. First call triggers the dynamic import and
 * kicks off the bundle fetch; subsequent calls reuse the cached
 * promise. Safe to call from feature-detect warm-up paths too.
 */
let threePromise: Promise<typeof import('three')> | null = null
export function loadThree(): Promise<typeof import('three')> {
  return (threePromise ??= import('three'))
}

/**
 * Handles for the current session. Single-session design — we don't
 * support entering VR twice concurrently, so a module-level ref is
 * fine and simplifies `isActive()` / teardown.
 */
interface ActiveSession {
  session: XRSession
  renderer: THREE.WebGLRenderer
  camera: THREE.PerspectiveCamera
  scene: VrSceneHandle
  hud: VrHudHandle
  /** Teardown hook for the DOM zoom slider — removes the
   *  `inputsourceschange` listener and disposes the overlay if one
   *  is currently mounted. Idempotent. Always present (a no-op for
   *  controller-only sessions that never mount the overlay). */
  disposeZoomOverlay: () => void
  interaction: VrInteractionHandle
  /** The side panel: Browse, Layers, Data, View and Help (vrSidePanel). Named for the browse panel it replaced. */
  browse: VrSidePanelHandle
  /** AYNI: a geo-media dataset's dots and panel. */
  geoMedia: VrGeoMediaHandle
  /** AYNI (DSA 1.8): each globe's vector layers, by globe mesh. */
  vectorLayers: Map<THREE.Object3D, VrVectorLayersHandle>
  /** AYNI (satellites plan): the orbit shell around the primary globe. */
  satellites: VrSatellitesHandle | null
  /** AYNI (satellites plan): "Sky above me", in passthrough AR only. */
  satelliteSky: VrSatelliteSkyHandle | null
  /** AYNI: the panels geo-media stands beside the globe (a picture, a label). */
  worldPanels: VrWorldPanelsHandle
  /** AYNI: the playing Khipu's (or lesson's) panel. */
  khipu: VrKhipuPanelHandle
  /** Floating date readout above the globe for datasets with time metadata. */
  timeLabel: VrTimeLabelHandle
  /**
   * Date track under the HUD — the primary dataset's declared time axis,
   * hidden while the host reports none. Distinct from `timeLabel`: the
   * label answers "what date is this?", the track answers "what is the
   * span, and where in it am I?", and only the track is interactive.
   */
  timeline: VrTimelineTrackHandle
  /** Loading scene shown during entry; null after fade-out + dispose. */
  loading: VrLoadingHandle | null
  /** AR-only spatial placement (hit-test reticle + Place button). Null when hit-test unavailable. */
  placement: VrPlacementHandle | null
  /** Reference space passed to per-frame hit-test resolution. */
  refSpace: XRReferenceSpace | null
  /**
   * The viewer-space hit-test source used by placement. Stored
   * here so the session-end teardown can explicitly cancel it and
   * release platform-side tracking resources.
   */
  hitTestSource: XRHitTestSource | null
}

let active: ActiveSession | null = null
/** AYNI: frames rendered since the debug panel last refreshed (its fps readout). */
let debugFrameCount = 0

/** A video's size and dropped / decoded frames, for the debug panel. */
function describeDecode(video: HTMLVideoElement | null): string {
  if (!video) return '-'
  const q = typeof video.getVideoPlaybackQuality === 'function' ? video.getVideoPlaybackQuality() : null
  const drops = q ? ` drop ${q.droppedVideoFrames}/${q.totalVideoFrames}` : ''
  return `${video.videoWidth}x${video.videoHeight}${video.paused ? ' paused' : ''}${drops}`
}

/** AYNI: the layer stack last handed to the scene; undefined = none yet this session. */
let appliedMapLayers: MapLayerImages | null | undefined = undefined

/**
 * Loading handover timings, in render-loop seconds. If texture readiness
 * has not arrived after LOADING_FALLBACK_S the splash is dismissed anyway
 * (an untextured globe beats an eternal splash); once it has arrived the
 * bar sits at 100% for LOADING_READY_PAUSE_S before the fade starts.
 */
const LOADING_FALLBACK_S = 10
const LOADING_READY_PAUSE_S = 0.25

/**
 * AYNI: the shared marker's frame in this session, once a scan has placed
 * the sphere from it; null before a scan. It is what lets the sphere's
 * orientation be said in terms every device in a room shares (see
 * getRoomGlobe / setFollowedRoomGlobe).
 *
 * AYNI (Quest review E3, 2026-11-03): a placement by hand since keeps it.
 * A hand Place moves the sphere and does not turn it, so the room's
 * heading still holds; dropping the frame used to swing every follower's
 * sphere to an unrelated heading when a lead re-placed. (Quest review E4:
 * its orientation follows the anchor's as the phone refines its map.)
 */
let markerFrame: { orientation: THREE.Quaternion } | null = null
/** AYNI: the lead's sphere, while this device follows one in a shared session. */
let followedGlobe: RoomGlobe | null = null

/**
 * AYNI: this session's sphere as a shared session describes it — its
 * orientation in the marker's frame when it was placed from the marker,
 * else in this session's own space — or null outside a session.
 */
export function getRoomGlobe(): RoomGlobe | null {
  if (!active) return null
  const q = active.scene.globe.quaternion.clone()
  if (markerFrame) q.premultiply(markerFrame.orientation.clone().invert())
  // AYNI (2026-10-22): the headset's zoom travels with the sphere (vrFocusZoom.ts).
  const zoom = getFocusZoom()
  return {
    q: [q.x, q.y, q.z, q.w],
    scale: active.scene.globe.scale.x,
    aligned: markerFrame !== null,
    ...(isZoomed(zoom) ? { zoom: { m: zoom.m, f: [...zoom.fd] as Vec3, c: [...zoom.c] as [number, number, number, number] } } : {}),
  }
}

/** AYNI (2026-10-22): whether two zooms draw the same, so a follower changes its own only when the lead's moved. */
function sameZoom(a: FocusZoomState, b: FocusZoomState): boolean {
  if (!isZoomed(a) && !isZoomed(b)) return true
  const near = (x: readonly number[], y: readonly number[]) => x.every((v, i) => Math.abs(v - y[i]!) < 1e-9)
  return Math.abs(a.m - b.m) < 1e-9 && near(a.fd, b.fd) && near(a.c, b.c)
}

/**
 * AYNI: make the sphere follow the lead's (applied each frame in the
 * render loop), or hand it back to this device's own controls with null.
 */
export function setFollowedRoomGlobe(globe: RoomGlobe | null): void {
  followedGlobe = globe
}

/** AYNI (Quest review D4): whether this device follows a meeting's lead (main.ts, from the room's status). */
let roomFollower = false
export function setXrRoomFollower(on: boolean): void {
  roomFollower = on
}

/** The largest size this device's own controls may give the globe: 2× for a follower with no lead's globe to follow. */
function globeScaleCeiling(): number {
  return roomFollower && !followedGlobe ? FOLLOWER_MAX_GLOBE_SCALE : MAX_GLOBE_SCALE
}

/**
 * AYNI (owner, 2026-10-22): the dock's Spin — the auto-rotate's rate
 * (vrSpin.ts), 0 while the globe is still. A session starts still; the
 * render loop turns the globe by it each frame, except while a trigger
 * holds the globe, a fly-to or a room's lead drives it, or the right
 * stick's zoom is on — the choice is kept and the spin resumes after.
 */
let spinRate = 0

/** The headset's auto-rotate rate, for checks. */
export function getSpinRate(): number {
  return spinRate
}

/** True while a VR session is live. */
export function isVrActive(): boolean {
  return active !== null
}

/**
 * In-flight flyTo animation driven by the render loop. A caller may
 * await the returned promise to act once the rotation settles. A new `flyToOnGlobe` call while one
 * is running resolves the previous one and replaces it — the last
 * call wins.
 */
interface PendingFlyTo {
  startQuat: THREE.Quaternion
  endQuat: THREE.Quaternion
  startTime: number
  durationMs: number
  resolve: () => void
}
let pendingFlyTo: PendingFlyTo | null = null

/**
 * Default flyTo animation length. Matches `MapRenderer.flyTo`'s
 * current 2.5 s pacing, so a camera move asked of both the page's
 * globe and the headset's settles at about the same instant in both.
 */
const FLY_TO_DEFAULT_DURATION_MS = 2500

/**
 * Rotate the VR globe so `(lat, lng)` faces the user's head. No-op
 * when no VR session is active.
 *
 * In 2D, `flyTo` moves the camera to look at a lat/lng. VR can't
 * move the user's head (WebXR owns the view transform — moving it
 * would induce motion sickness), so we rotate the globe instead:
 * the point on the sphere corresponding to `(lat, lng)` rotates to
 * the surface position closest to the user's current head position.
 *
 * Captured at animation start:
 * - `startQuat`: globe's current orientation.
 * - `endQuat`: orientation that maps the local unit vector of
 *   `(lat, lng)` onto the world-space direction from globe center
 *   to the camera. If the user walks to the other side of the
 *   globe in AR mode, re-calling `flyToOnGlobe` captures the new
 *   head position and rotates accordingly.
 *
 * Slerp + ease-in-out drives the interpolation each frame until the
 * duration elapses, at which point the returned promise resolves.
 */
export async function flyToOnGlobe(
  lat: number,
  lng: number,
  durationMs: number = FLY_TO_DEFAULT_DURATION_MS,
): Promise<void> {
  if (!active) return
  const THREE_ = await loadThree()
  if (!active) return // session may have ended between await + resume

  // Target direction in world space = unit vector from globe center
  // to the user's head. `camera.getWorldPosition` accounts for the
  // XR view matrix, so this is the actual user position in
  // local-floor coords (not a fixed nominal spot).
  // AYNI (2026-10-22): a fly-to (a Khipu step, a place) starts from the
  // whole Earth: the headset's zoom goes back to 1× first, its map rotation
  // folded into the globe's so nothing jumps.
  const fold = resetZoom(getFocusZoom())
  if (fold) {
    active.scene.globe.quaternion.multiply(new THREE_.Quaternion(fold[0], fold[1], fold[2], fold[3]))
    commitFocusZoom()
  }

  const camPos = new THREE_.Vector3()
  active.camera.getWorldPosition(camPos)
  const worldDir = camPos.clone().sub(active.scene.globe.position).normalize()

  // Local-space target point for (lat, lng). Matches the
  // convention `photorealEarth.sunDirectionFromLatLng` uses
  // internally so the orientation lines up with the dataset /
  // photoreal Earth texture's equirectangular wrap on the sphere
  // (including the negated Z that Three.js SphereGeometry's
  // default phiStart introduces).
  const latRad = (lat * Math.PI) / 180
  const lngRad = (lng * Math.PI) / 180
  const localTarget = new THREE_.Vector3(
    Math.cos(latRad) * Math.cos(lngRad),
    Math.sin(latRad),
    -Math.cos(latRad) * Math.sin(lngRad),
  )

  // Resolve any prior flyTo first so callers that awaited it don't
  // hang waiting on a superseded animation.
  if (pendingFlyTo) {
    const prior = pendingFlyTo
    pendingFlyTo = null
    prior.resolve()
  }

  return new Promise<void>((resolve) => {
    pendingFlyTo = {
      startQuat: active!.scene.globe.quaternion.clone(),
      endQuat: new THREE_.Quaternion().setFromUnitVectors(localTarget, worldDir),
      startTime: performance.now(),
      durationMs,
      resolve,
    }
  })
}

/** Cancel any in-flight flyTo animation — used by session teardown. */
function cancelFlyTo(): void {
  if (!pendingFlyTo) return
  const prior = pendingFlyTo
  pendingFlyTo = null
  prior.resolve()
}

/**
 * Push the non-primary panels' textures into scene slots 1..N-1.
 * Scene slot 0 holds the 2D app's primary panel (set via
 * `scene.setTexture`); the remaining scene slots are filled from the
 * 2D panel list in order, skipping the primary index. Separated so
 * the initial-setup path and the per-frame poll path stay in sync.
 */
function syncSecondaryTextures(
  scene: VrSceneHandle,
  ctx: VrSessionContext,
  panelCount: number,
): void {
  if (panelCount <= 1) return
  const primary = ctx.getPrimaryIndex()
  let sceneSlot = 1
  for (let panelSlot = 0; panelSlot < panelCount; panelSlot++) {
    if (panelSlot === primary) continue
    scene.setSlotTexture(sceneSlot, ctx.getPanelTexture(panelSlot))
    sceneSlot++
  }
}

/**
 * AYNI (satellites plan): the device's place for "Sky above me", if the
 * viewer allows it; null otherwise (the sky then asks for a tap on the
 * globe). Used once per sky, kept in the session only, never sent.
 */
function deviceLocation(): Promise<Observer | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null)
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latDeg: p.coords.latitude, lonDeg: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 10 * 60_000 },
    )
  })
}

/** AYNI (satellites plan): the selected satellite's card, on the globe's left (vector panels stand on its right). */
const SATELLITE_CARD_ID = 'satellite-card'
const SATELLITE_CARD_PLACE = { offset: { x: -0.95, y: 0.12, z: 0.15 }, size: { width: 0.78, height: 0.62 } }

/** AYNI (DSA 1.8): a tapped vector feature's panel, beside the globe where geo-media's picture goes. */
const VECTOR_INSPECT_ID = 'vector-inspect'
const VECTOR_INSPECT_PLACE = { offset: { x: 0.95, y: 0.1, z: 0.15 }, size: { width: 0.8, height: 0.34 } }

let vectorScratch: { cam: THREE.Vector3; centre: THREE.Vector3; scale: THREE.Vector3 } | null = null

/**
 * AYNI (DSA 1.8): each globe's vector layers, from the host's players, in
 * the scene's globe order (the primary first, then the other slots in
 * order, as syncSecondaryTextures), with the px size of §7.2 for XR
 * worked out from how far the viewer is from each globe.
 */
function syncVrVectorLayers(THREE_: typeof THREE, a: ActiveSession, ctx: VrSessionContext, panelCount: number): void {
  const frames = ctx.getVectorFrames?.() ?? null
  if (!frames && a.vectorLayers.size === 0) return
  const primary = ctx.getPrimaryIndex()
  const order = [primary]
  for (let slot = 0; slot < panelCount; slot++) if (slot !== primary) order.push(slot)
  const globes = a.scene.allGlobes
  vectorScratch ??= { cam: new THREE_.Vector3(), centre: new THREE_.Vector3(), scale: new THREE_.Vector3() }
  const { cam, centre, scale } = vectorScratch
  a.camera.getWorldPosition(cam)
  const seen = new Set<THREE.Object3D>()
  order.forEach((panelSlot, sceneSlot) => {
    const globe = globes[sceneSlot]
    const frame = frames?.[panelSlot]
    if (!globe || !frame) return
    seen.add(globe)
    let layers = a.vectorLayers.get(globe)
    if (!layers) {
      layers = createVrVectorLayers(THREE_, globe, GLOBE_RADIUS, getLocale)
      a.vectorLayers.set(globe, layers)
    }
    globe.getWorldPosition(centre)
    const worldRadius = GLOBE_RADIUS * globe.getWorldScale(scale).x
    // AYNI (owner, 2026-10-12): the primary dataset's transparency and eye reach its vector layers too.
    const look = panelSlot === primary ? ctx.getDatasetLook?.() : null
    // AYNI (2026-10-22): hidden while the headset's zoom is on; their lines are not cut finely enough to bend with it.
    const zoomDim = isZoomed(getFocusZoom()) ? 0 : 1
    layers.update(frame.layers, frame.version, pxRadAt(centre.distanceTo(cam) - worldRadius, worldRadius), (look ? (look.hidden ? 0 : look.opacity) : 1) * zoomDim)
  })
  for (const [globe, layers] of a.vectorLayers) {
    if (seen.has(globe)) continue
    layers.dispose()
    a.vectorLayers.delete(globe)
  }
}

/**
 * AYNI (2026-10-22): whether the dataset shown is finer than 200 m a pixel,
 * which lets the headset's zoom go to 9.5 as the browser's does
 * (globeZoomCeiling.ts).
 */
function fineDatasetShown(spec: VrDatasetTexture | null): boolean {
  if (!spec) return false
  const el = spec.element as { videoWidth?: number; videoHeight?: number; naturalWidth?: number; naturalHeight?: number; width?: number; height?: number }
  const width = el.videoWidth || el.naturalWidth || el.width || 0
  const height = el.videoHeight || el.naturalHeight || el.height || 0
  return width > 0 && height > 0 && isFineGrid({ width, height }, spec.options)
}

/** Which immersive mode to enter. `vr` = full immersive, `ar` = passthrough. */
export type VrMode = 'vr' | 'ar'

/**
 * Request an immersive WebXR session (VR or AR passthrough), build
 * the Three.js scene, attach controllers, and start the render loop.
 * Rejects if the browser refuses the session (user denied permission,
 * no device, etc.). On success, resolves once the session is fully
 * live — the user will be in the headset at that point.
 *
 * Calling while a session is already active is a no-op.
 *
 * AR mode (`mode === 'ar'`) requests `immersive-ar` and configures
 * the renderer + scene for transparent rendering, so the Quest
 * passthrough camera feed shows behind the floating globe and HUD.
 * Visually identical to VR mode for everything in the scene; only
 * the surrounding "void" changes from black to the user's room.
 *
 * Called through `enterImmersive` (below), which cleans up a failed build.
 */
async function buildImmersive(mode: VrMode, ctx: VrSessionContext): Promise<void> {
  appliedMapLayers = undefined
  debugFrameCount = 0
  markerFrame = null
  if (active) {
    logger.warn(`[VR] enterImmersive(${mode}) called while a session is already active`)
    return
  }
  if (!navigator.xr) {
    throw new Error('WebXR is not available in this browser')
  }

  // Wall-clock anchor for `vr_session_started.entry_load_ms` and the
  // matching `vr_session_ended.duration_ms`. Captured before the
  // Three.js chunk load so a slow first-time fetch shows up in the
  // entry-load metric.
  const entryStartedAtWall = Date.now()
  const sessionTelemetry: {
    sessionStartedAtWall: number
    frames: number
    exitReason: VrExitReason
    /** Coarse input archetype the device is using. Resolves lazily —
     * screen-tap (handheld AR) sessions report zero input sources
     * until the first tap, so this starts as `'unknown'` and is
     * updated by the `inputsourceschange` listener wired below.
     * Consumed by future PRs to gate alternative UX (DOM zoom
     * slider, HUD exit button) for non-controller devices. */
    inputClass: VrInputArchetype
  } = {
    sessionStartedAtWall: 0,
    frames: 0,
    exitReason: 'user',
    inputClass: 'unknown',
  }

  const THREE_ = await loadThree()
  const isAr = mode === 'ar'
  const sessionMode = isAr ? 'immersive-ar' : 'immersive-vr'

  // --- Renderer + canvas ---
  // The canvas doesn't display anything while the session is live
  // (the headset takes over), but Three.js still needs a DOM-
  // attached canvas for the WebGL context to behave correctly. We
  // inject a small offscreen-like host and remove it on teardown.
  const canvas = document.createElement('canvas')
  canvas.id = 'vr-canvas'
  canvas.style.position = 'absolute'
  canvas.style.inset = '0'
  canvas.style.pointerEvents = 'none'
  // Hide under the existing 2D UI so it doesn't flash during the
  // transition. Display:none would prevent GL context creation, so
  // use zero opacity instead.
  canvas.style.opacity = '0'
  document.body.appendChild(canvas)

  const renderer = new THREE_.WebGLRenderer({
    canvas,
    antialias: true,
    // AR passthrough requires alpha so the framebuffer can clear to
    // transparent and reveal the camera feed; VR keeps it disabled
    // for a slight performance edge (one less blend pass per pixel).
    alpha: isAr,
    // State a preference for the discrete GPU on a hybrid-graphics
    // machine. Three.js leaves this at `'default'`, and MapLibre already
    // asks for `'high-performance'`, so absent this the 2D globe and the
    // immersive globe are asking for different things on one machine.
    //
    // **It is a hint, and it is not always honoured.** Measured on a
    // Windows box with an RTX 4090: an unhinted context came back as
    // `ANGLE (Intel, Intel(R) UHD Graphics …)`, and so did a context
    // that asked for `'high-performance'`. Chrome appears to pick one
    // GPU for its whole GPU process, so a per-context request cannot
    // move it; what moved it was the Windows per-app graphics
    // preference. So this option does not guarantee an adapter, and on
    // that platform it may do nothing at all.
    //
    // What it buys where it *is* honoured: `WebXRManager.setSession`
    // awaits `gl.makeXRCompatible()`, which migrates the context to the
    // adapter driving the headset. That migration can force a context
    // loss and restore — every texture, including the dataset video and
    // the whole photoreal Earth stack, destroyed and re-uploaded —
    // landing inside the session-start ordering this file documents as
    // already delicate (see the `XRControllerModelFactory` note above).
    // A context created on that adapter has nothing to migrate. Where
    // the hint is ignored, `makeXRCompatible` still handles correctness;
    // this only removes a cost, and only sometimes.
    //
    // Deliberately *not* applied to the Orbit character page: that scene
    // never enters XR, so it gains nothing here, and forcing a laptop
    // onto its discrete GPU for a decorative idle animation is a real
    // battery cost for no user-visible benefit.
    powerPreference: 'high-performance',
  })
  renderer.setPixelRatio(window.devicePixelRatio)
  renderer.setSize(window.innerWidth, window.innerHeight)
  renderer.xr.enabled = true

  // Camera: a default perspective is fine — Three.js' XR layer
  // overrides projection / view matrices from the XR views, so
  // these values are only used for inline rendering (which we skip).
  const camera = new THREE_.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.01, 100)

  // --- Request the session ---
  // hit-test is requested as OPTIONAL for AR sessions so the
  // session still starts on browsers/devices without the feature
  // — placement just won't be available in that case. Skipped
  // entirely for VR (no real-world geometry to hit-test against).
  const optionalFeatures: string[] = []
  if (isAr) {
    optionalFeatures.push('hit-test')
    // Anchors let us bolt the globe to a real-world surface and
    // have it stay there across tracking adjustments (within a
    // session) and across sessions (via Meta's persistent-handle
    // extension). Core to the "globe actually stays on my table"
    // UX. Optional because not all UAs implement anchors yet.
    optionalFeatures.push('anchors')
    // dom-overlay lets the browser render a DOM subtree on top of
    // the handheld-AR camera feed — without it the in-DOM zoom
    // slider / placement touch layer never becomes visible during
    // the session. Optional because Quest browsers don't implement
    // it and must not fail the request.
    optionalFeatures.push('dom-overlay')
    // AYNI: the marker scan reads the camera picture (vrMarkerAlign).
    // Asked for only on a phone that opened the page with `?marker=1`,
    // because the browser puts a camera prompt in front of the session
    // for it; headset browsers do not offer it at all.
    if (isMarkerAlignRequested() && isHandheldArUserAgent(navigator.userAgent)) {
      // (A page in a shared session always asks: see isMarkerAlignRequested.)
      optionalFeatures.push('camera-access')
    }
  }
  // Root element handed to the dom-overlay module. Chrome on Android
  // renders this subtree over the camera feed; see index.html.
  const domOverlayRoot = isAr ? document.getElementById('xr-dom-overlay') : null
  /**
   * Build the session init for one request attempt. `domOverlay`
   * isn't in the TS DOM lib's XRSessionInit yet — same
   * `as unknown as` pattern as the hit-test / anchor requests below.
   */
  const buildSessionInit = (required: string[]): XRSessionInit =>
    ({
      requiredFeatures: required,
      ...(optionalFeatures.length > 0 ? { optionalFeatures } : {}),
      ...(domOverlayRoot ? { domOverlay: { root: domOverlayRoot } } : {}),
    }) as unknown as XRSessionInit
  // Reference-space contract for the rest of the session. `local-floor`
  // gives the globe a stable Y above the user's actual floor; `local`
  // anchors at the head pose at session start with no floor offset.
  // The session-features list, the default GLOBE_POSITION, and the
  // placement reference space all key off this — keep them consistent.
  let referenceSpaceType: 'local-floor' | 'local' = 'local-floor'
  let session: XRSession
  try {
    session = await navigator.xr.requestSession(sessionMode, buildSessionInit(['local-floor']))
  } catch (err) {
    // Only retry on a feature-unsupported failure (HoloLens 2 Edge in
    // early builds, certain WebXR polyfills). Permission denial,
    // transient hardware errors, and any other failure mode propagate
    // as-is — re-prompting the user a second time would be both
    // annoying and confusing, and would mask the original error. The
    // narrowed retry also keeps `vr_session_started.entry_load_ms`
    // honest on the user-denial path (otherwise a deny → retry →
    // deny would double the latency).
    const isFeatureUnsupported =
      err instanceof DOMException && err.name === 'NotSupportedError'
    if (!isFeatureUnsupported) {
      canvas.remove()
      releaseRenderer(renderer)
      throw err instanceof Error ? err : new Error(String(err))
    }
    logger.debug('[VR] local-floor unavailable, retrying with local:', err)
    try {
      session = await navigator.xr.requestSession(sessionMode, buildSessionInit(['local']))
      referenceSpaceType = 'local'
    } catch (retryErr) {
      canvas.remove()
      releaseRenderer(renderer)
      throw retryErr instanceof Error ? retryErr : new Error(String(retryErr))
    }
  }

  // AYNI (Quest review C8): three asks for its own reference space type
  // (local-floor by default), so a `local` retry must be told to it.
  renderer.xr.setReferenceSpaceType(referenceSpaceType)

  // AYNI (Quest review C1): an end while the session is still being built
  // (the Meta button while the headset is black, a dropped session) comes
  // before the full teardown below is listening. Note it here; the
  // teardown runs as soon as it is in place, and `enterImmersive` cleans
  // up when the build fails, so the next Enter works.
  let endedWhileStarting = false
  const onEndWhileStarting = (): void => {
    endedWhileStarting = true
  }
  session.addEventListener('end', onEndWhileStarting)
  starting = {
    session,
    ended: () => endedWhileStarting,
    release: () => {
      renderer.setAnimationLoop(null)
      releaseRenderer(renderer)
      canvas.remove()
    },
  }

  // Bind the session to the renderer. Three.js handles
  // makeXRCompatible + baseLayer setup internally.
  try {
    await renderer.xr.setSession(session as unknown as XRSession)
  } catch (err) {
    sessionTelemetry.exitReason = 'error'
    starting = null
    await session.end().catch(() => { /* already gone */ })
    canvas.remove()
    releaseRenderer(renderer)
    throw err instanceof Error ? err : new Error(String(err))
  }

  // AYNI (Quest review D10, 2026-11-03): an explicit frame rate instead of
  // whatever the headset defaults to. 72 Hz is steady on a Quest and leaves
  // the most room per frame; headsets that don't offer it keep their own.
  const frameRates = (session as unknown as { supportedFrameRates?: Float32Array }).supportedFrameRates
  const setFrameRate = (session as unknown as { updateTargetFrameRate?: (rate: number) => Promise<void> }).updateTargetFrameRate
  if (frameRates && setFrameRate && Array.from(frameRates).includes(TARGET_FRAME_RATE)) {
    setFrameRate.call(session, TARGET_FRAME_RATE).then(
      () => logger.info(`[VR] frame rate ${TARGET_FRAME_RATE} Hz (offered: ${Array.from(frameRates).join(', ')})`),
      (err: unknown) => logger.debug('[VR] updateTargetFrameRate refused:', err),
    )
  }

  // The session is bound; emit `vr_session_started` now so
  // entry_load_ms reflects the user-perceived "tap → in-VR" latency
  // including the Three.js chunk load + setSession round-trip. The
  // layer_id snapshot is the dataset loaded at entry time; if the
  // user loads a different dataset mid-session the
  // `vr_session_ended` event captures the post-change value.
  sessionTelemetry.sessionStartedAtWall = Date.now()
  emit({
    event_type: 'vr_session_started',
    mode,
    device_class: classifyXrDevice(
      typeof navigator !== 'undefined' ? navigator.userAgent : '',
      mode,
    ),
    entry_load_ms: Math.max(0, sessionTelemetry.sessionStartedAtWall - entryStartedAtWall),
    layer_id: ctx.getDatasetId() ?? '',
  })

  // Resolve the input archetype lazily. Handheld-AR sessions (Android
  // + ARCore) report zero input sources at session start — a
  // transient `screen` source only appears on the user's first tap —
  // so an at-start snapshot would always read `'unknown'` and the
  // dependent UX (DOM zoom slider, HUD exit button) would never
  // mount. Re-resolving on every `inputsourceschange` lets the
  // archetype settle as inputs come and go (controllers wake on
  // pickup, hand-tracking toggles in/out on Quest, transient pointers
  // fire per pinch on Vision Pro).
  const updateInputClass = (): void => {
    let next = getInputArchetype(session)
    // Latch the `screen` archetype for the session lifetime: the
    // transient screen input source only exists for the duration of
    // a tap, so every tap release fires `inputsourceschange` back to
    // zero sources (`unknown`). Without the latch, dependent UI
    // (the DOM zoom overlay) would unmount/remount on every tap.
    // Controller / transient archetypes still recompute freely — a
    // real controller appearing later still wins over the latch.
    if (sessionTelemetry.inputClass === 'screen' && next === 'unknown') {
      next = 'screen'
    }
    if (next !== sessionTelemetry.inputClass) {
      logger.debug(
        `[VR] inputClass: ${sessionTelemetry.inputClass} -> ${next}`,
      )
      sessionTelemetry.inputClass = next
    }
  }
  updateInputClass()
  session.addEventListener('inputsourceschange', updateInputClass)

  /**
   * Handheld or headset? Decided ONCE for the session, from the session's
   * interactionMode when the browser reports it, else from the user
   * agent, and never recomputed — see `isHandheldArUserAgent` for why
   * the gamepad-presence test it replaces flapped on every phone tap.
   *
   *   - handheld (Android phone / tablet, Chrome + ARCore): the DOM
   *     placement chrome, the zoom slider, a one-finger rotate, and
   *     NOTHING else from touch. The XR pinch, grab and thumbstick
   *     paths in vrInteraction all stand down for the whole session.
   *   - headset (Quest 3 / 3S / Pro, Pico, …): the controller model
   *     exactly as before — grab-and-rotate, two-hand pinch,
   *     thumbstick zoom, trigger-to-confirm placement.
   */
  // AYNI (Quest review D2): the session's interactionMode first, then the user agent.
  const handheldAr = isAr && isHandheldArSession(session as { interactionMode?: string }, navigator.userAgent)
  /** Debug readout only — no gate keys off this any more. */
  const hasGamepadInput = (): boolean => {
    for (const source of session.inputSources) {
      if (source?.gamepad) return true
    }
    return false
  }
  /**
   * True once the handheld rotate layer is mounted and owns the globe's
   * rotation. vrInteraction declines its own globe grab while this is
   * set, so one drag cannot rotate through both paths.
   */
  let rotateTouchMounted = false

  // Whether the session actually granted the DOM overlay. Requested
  // as optional above, so Quest browsers (which don't implement the
  // dom-overlays module) silently skip it and this reads false —
  // every DOM-dependent affordance below must gate on it, not on
  // the request alone. `domOverlayState` isn't in the TS DOM lib
  // yet — same cast pattern as the other optional-feature reads.
  const domOverlayActive =
    isAr &&
    domOverlayRoot !== null &&
    (session as unknown as { domOverlayState?: { type?: string } })
      .domOverlayState?.type === 'screen'
  /**
   * AYNI (the AR refresh, 2026-10-04): a phone with the overlay gets its
   * controls there (src/ui/xrPhoneChrome.ts) — top line, legend,
   * transport and dock — and none of the 3D dock, which on a phone sat a
   * metre and a half away and often off the screen.
   */
  const phoneUi = handheldAr && domOverlayActive

  // One line that answers "why did the AR gestures not engage?" without
  // a device attached: the resolved archetype, how many input sources the
  // session reports, whether any of them carries a gamepad (on a phone
  // it does — that is the touch position, not a controller), whether
  // the browser granted the DOM overlay, and the handheld/headset
  // decision every gate downstream keys off.
  logger.info(
    `[VR] input at start: class=${sessionTelemetry.inputClass} ` +
      `sources=${session.inputSources.length} gamepad=${hasGamepadInput()} ` +
      `domOverlay=${domOverlayActive} handheld=${handheldAr}`,
  )

  // Lazy-load the controller-model addon alongside Three.js. The
  // factory fetches per-controller glTF models from a CDN at runtime
  // (e.g. Quest Touch), so the addon itself is small but enables a
  // big polish win: users see their actual controllers in VR.
  //
  // IMPORTANT: this import must complete before `setTexture`'s
  // synchronous onReady callback can schedule the loading-scene
  // fade-out. Historically the fade ran on a 250 ms setTimeout, so a cold-cache
  // download that outran it left `active` null when it fired and the
  // loading scene stuck visible. The handover is loop-driven now, but the
  // ordering is kept: the import resolves before `active` is needed and
  // before the first frame can run the handover.
  const { XRControllerModelFactory } = await import(
    'three/examples/jsm/webxr/XRControllerModelFactory.js'
  )

  // --- Build the scene ---
  // AR mode → transparent background so the passthrough camera feed
  // shows behind everything we render.
  const scene = createVrScene(THREE_, isAr, referenceSpaceType)
  const hud = createVrHud(THREE_)
  scene.scene.add(hud.mesh)

  // AYNI (the AR refresh): the side panel — Browse, Layers, Data, View and
  // Help, one at a time to the right of the globe — reading and changing
  // the app's own state through the host's tools.
  const tools = ctx.tools ?? null
  const browse = createVrSidePanel(THREE_, {
    datasets: () => ctx.getDatasets(),
    recentIds: () => tools?.recentIds() ?? [],
    search: (query) => tools?.search(query) ?? null,
    currentId: () => ctx.getDatasetId(),
    layers: () => tools?.layers() ?? null,
    satellite: (action) => tools?.satellite?.(action),
    view: () => tools?.view() ?? null,
    pins: () => tools?.pins() ?? null,
    loadDataset: (id) => ctx.loadDataset(id),
    lockedChosen: (pkg, title) => tools?.lockedStream(pkg, title),
    setOverlay: (id) => tools?.setOverlay(id),
    setTint: (id, tint) => tools?.setTint(id, tint),
    toggleRt: (id) => tools?.toggleRt(id),
    setRtHidden: (id, hidden) => tools?.setRtHidden(id, hidden),
    moveRt: (id, to) => tools?.moveRt(id, to),
    setRtPalette: (id, palette) => tools?.setRtPalette(id, palette),
    stepRtLimit: (id, end, dir) => tools?.stepRtLimit(id, end, dir),
    setRtInverted: (id, on) => tools?.setRtInverted(id, on),
    stepRtOpacity: (id, dir) => tools?.stepRtOpacity(id, dir),
    setBaseHidden: (hidden) => tools?.setBaseHidden(hidden),
    closeBase: () => tools?.closeBase(),
    stepBaseOpacity: (dir) => tools?.stepBaseOpacity(dir),
    setPalette: (id) => tools?.setPalette(id),
    setInverted: (on) => tools?.setInverted(on),
    resetView: () => tools?.resetView(),
    setLegendParked: (parked) => tools?.setLegendParked(parked),
    removePin: (id) => tools?.removePin(id),
    renamePin: (id, name) => tools?.renamePin(id, name),
    clearPins: () => tools?.clearPins(),
    markExtremes: () => tools?.markExtremes(),
    toggleFollow: () => tools?.toggleFollow(),
    toggleTrack: () => tools?.toggleTrack(),
    setSurface: (mode) => tools?.setSurface(mode),
  })
  scene.scene.add(browse.mesh)
  // The legend beside the globe, the pins on it, and the value at the ray's tip.
  const legendPanel: VrLegendHandle = createVrLegend(THREE_)
  scene.scene.add(legendPanel.mesh)
  const pinMarks: VrPinsHandle = createVrPins(THREE_, scene.globe, GLOBE_RADIUS, scene.scene)
  // AYNI (owner, 2026-10-22): the right stick's zoom, from the whole Earth each session.
  clearFocusZoom()
  const focusZoomView = createVrFocusZoomView(THREE_, scene, GLOBE_RADIUS)
  const readout: VrReadoutHandle = createVrReadout(THREE_)
  scene.scene.add(readout.mesh)
  /** A short message over the dock's title row, and when it goes. */
  let notice: { text: string; until: number } | null = null

  // Date track — the timeline of the primary dataset's declared time
  // axis. Added now and hidden until the host reports one, because most
  // datasets declare none and a session that never loads a real-time
  // stream should pay nothing for it beyond one invisible mesh.
  const timeline = createVrTimelineTrack(THREE_)
  scene.scene.add(timeline.mesh)

  // AYNI: a geo-media dataset's dots on the globe and its panel beside it,
  // on AYNI's own world panels (since 2026-10-07).
  const worldPanels = createVrWorldPanels(THREE_)
  scene.scene.add(worldPanels.group)
  const geoMedia = createVrGeoMedia(THREE_, scene.globe, GLOBE_RADIUS, worldPanels)
  // AYNI: the Khipu (or lesson) playing, beside the globe; hidden until the host reports one.
  const khipuPanel = createVrKhipuPanel(THREE_)
  scene.scene.add(khipuPanel.mesh)
  let appliedGeoMedia: VrGeoMediaState | null = null
  /** AYNI (DSA 1.8): the inspected vector feature whose panel is up, by key. */
  let shownVectorInspection: string | null = null
  // AYNI (satellites plan): the orbit shell, and the selected satellite's card beside the globe.
  const satelliteLayer = ctx.getSatellites?.() ?? null
  const satellites = satelliteLayer
    ? createVrSatellites(THREE_, scene.globe, GLOBE_RADIUS, satelliteLayer, { visible: () => !satelliteLayer.skyOn && !isZoomed(getFocusZoom()) })
    : null
  // AYNI (satellites plan): "Sky above me" needs the real sky behind it: passthrough AR only.
  const satelliteSky = satelliteLayer && isAr
    ? createVrSatelliteSky(THREE_, scene.scene, satelliteLayer, {
        handheld: handheldAr,
        geolocate: deviceLocation,
      })
    : null
  // AYNI (Fully VR, owner 2026-11-03): floating in space. The stars, the Milky
  // Way and the Sun all the way round the viewer (no ground, no horizon), tied
  // to the globe: the sky's turn is the globe's (with the zoom's fold) times
  // the Earth's turn among the stars at the satellites' clock. No place or
  // north, and no turning the globe on entering: the globe stays as it is and
  // the sky stands true around it. Turning either turns both.
  const nightSkyRoot = satelliteSky ? new THREE_.Group() : null
  if (nightSkyRoot) {
    nightSkyRoot.name = 'full-vr-sky'
    scene.scene.add(nightSkyRoot)
  }
  const nightSky = nightSkyRoot ? createVrNightSky(THREE_, nightSkyRoot) : null
  const fullVrGlobeQ = new THREE_.Quaternion()
  const fullVrFoldQ = new THREE_.Quaternion()
  const fullVrM4 = new THREE_.Matrix4()
  /** AYNI (Fully VR): whether the globe was lit at the sky's clock last frame. */
  let fullVrWas = false
  /** Fully VR with its sky drawn: the globe and the sky move as one. */
  const fullVrCoupled = (): boolean => !!(satelliteLayer?.fullVrOn && nightSky?.ready)
  /** The sky's turn (row-major 3×3, celestial → room) for the globe as it stands now. */
  const fullVrSkyTurn = (timeMs: number): number[] => {
    const c = getFocusZoom().c
    fullVrGlobeQ.copy(scene.globe.quaternion).multiply(fullVrFoldQ.set(c[0], c[1], c[2], c[3]))
    return skyTurnForGlobe(fullVrM4, fullVrGlobeQ, timeMs)
  }
  satelliteLayer?.setSkyAvailable(!!satelliteSky)
  let northAsked = satelliteLayer?.northRequest ?? 0
  let shownSatellite: number | null = null
  let satelliteCardAt = 0
  const satelliteViewport = new THREE_.Vector2()

  // Floating date readout above the globe. Hidden by default;
  // per-frame setText(ctx.getDatasetTimeLabel()) flips it on when
  // a time-metadata-bearing dataset is loaded and drives it
  // forward each XR frame (2D's playback loop is paused during
  // the session so we can't rely on appState.timeLabel — we
  // recompute from video.currentTime directly instead).
  const timeLabel = createVrTimeLabel(THREE_)
  scene.scene.add(timeLabel.mesh)
  // --- Spatial placement (AR-only) + local-floor ref space ---
  // Two separable capabilities:
  //
  //   (a) local-floor reference space — used for resolving ANCHOR
  //       poses each frame (`frame.getPose(anchor.anchorSpace,
  //       refSpace)`). An anchor restored from a persistent handle
  //       needs this even if the user never enters Place mode in
  //       the current session. Requesting it independently means
  //       anchor-based placement keeps working on devices that
  //       expose anchors but not hit-test.
  //
  //   (b) hit-test source — used by Place mode to project a
  //       reticle onto real-world geometry. Optional; older
  //       browsers may not support it. When unavailable, the
  //       Place button stays hidden but restored anchors still
  //       track via (a).
  //
  // Both are AR-only; VR sessions have no real-world geometry.
  let hitTestSource: XRHitTestSource | null = null
  let placementRefSpace: XRReferenceSpace | null = null
  if (isAr) {
    try {
      // Use the same reference space the session was actually
      // granted. Requesting `local-floor` here when the session was
      // granted only `local` would fail every time on the fallback
      // path and leave anchor restoration silently broken.
      placementRefSpace = await session.requestReferenceSpace(referenceSpaceType)
    } catch (err) {
      logger.debug(`[VR] ${referenceSpaceType} reference space unavailable:`, err)
    }

    if ('requestHitTestSource' in session) {
      try {
        const viewerSpace = await session.requestReferenceSpace('viewer')
        // requestHitTestSource is on the session interface but not
        // in all type defs; cast to get a typed handle.
        const reqHts = (session as unknown as {
          requestHitTestSource?: (init: { space: XRReferenceSpace }) => Promise<XRHitTestSource>
        }).requestHitTestSource
        if (reqHts) {
          hitTestSource = await reqHts.call(session, { space: viewerSpace })
        }
      } catch (err) {
        logger.debug('[VR] hit-test setup failed; spatial placement disabled:', err)
      }
    }
  }
  const placement = createVrPlacement(THREE_, isAr ? 'ar' : 'vr', hitTestSource)
  // AR offers the Place button only when a hit-test source backs it; VR
  // always has a gaze ray to project, so the button always shows. It
  // stays hidden until the loading handover reveals the scene — see
  // `syncPlacementChrome` — because a live Place affordance over a
  // hidden globe invites a phone to anchor something nobody can see.
  const placementAvailable = isAr ? !!hitTestSource : true
  if (placement) {
    scene.scene.add(placement.reticleGroup)
    scene.scene.add(placement.railGroup)
    scene.scene.add(placement.placeButtonMesh)
  }

  // --- Placement: surface, hold and anchor (vrGlobePlacement.ts) ---
  // See src/utils/vrPersistence.ts for why we use anchors instead of
  // saved coordinates: Quest's local-floor space is re-based every
  // session, so a saved (x, y, z) corresponds to a different
  // physical location each time. Anchors are system-tracked and
  // stable across sessions.
  //
  // AYNI (Quest review E1, E2, E4, B7, 2026-11-03): the anchor, the hold
  // that keeps the globe's height above its surface at any size, the
  // marker's heading following the anchor, and the restore check live in
  // vrGlobePlacement.ts, where they are tested against fake XR frames.
  const globePlacement = createGlobePlacement(THREE_, {
    globe: scene.globe,
    isAr,
    radius: () => GLOBE_RADIUS * scene.globe.scale.x,
    markerHeading: () => markerFrame?.orientation ?? null,
    isLive: () => active?.session === session,
    onPlacedByHand: (persisted) => emit({ event_type: 'vr_placement', layer_id: ctx.getDatasetId() ?? '', persisted }),
    savePlacement: savePersistedAnchorHandle,
    clearSavedPlacement: clearPersistedAnchorHandle,
    onOutOfReach: () => {
      pendingGazeSpawn = true
      showNotice(t('vr.place.broughtHere'))
    },
  })
  /** Whether the scan's outline belongs where the sphere is (a marker placement, not a hand Place since). */
  let placedByMarker = false
  // AYNI: the marker scan. Null unless the session granted the camera
  // picture, and useless without a surface to meet the marker on.
  const markerAlign =
    hitTestSource && placementRefSpace
      ? createVrMarkerAlign(session, renderer.getContext())
      : null
  let markerTouch: VrMarkerAlignTouchHandle | null = null
  // Where a scan put the marker, drawn for a few seconds so the person
  // holding the phone can see whether it matches the real one.
  const markerOutline = markerAlign ? createVrMarkerOutline(THREE_) : null
  if (markerOutline) scene.scene.add(markerOutline.mesh)
  if (isAr) {
    // AYNI (Quest review B7): the handle is saved with its hold, so a
    // re-entered globe comes back at the height it was left at, not
    // half-buried in its surface.
    const raw = loadPersistedAnchorHandle()
    const saved = decodeSavedPlacement(raw)
    if (raw && !saved) clearPersistedAnchorHandle()
    if (saved) {
      const restoreFn = (session as unknown as {
        restorePersistentAnchor?: (uuid: string) => Promise<XRAnchor>
      }).restorePersistentAnchor
      if (restoreFn) {
        try {
          globePlacement.restore(await restoreFn.call(session, saved.handle), saved.hold)
          logger.info('[VR] Restored persistent placement anchor')
        } catch (err) {
          logger.warn('[VR] Failed to restore persistent anchor; clearing handle:', err)
          clearPersistedAnchorHandle()
        }
      }
    }
  }

  // --- Loading scene ---
  // Visible from the moment the session starts until the dataset
  // texture has a decoded frame on the globe. Hides the real globe
  // + HUD initially so the user sees a clean transition.
  const loading = createVrLoading(THREE_)
  scene.scene.add(loading.group)
  scene.setEarthVisible(false)
  hud.mesh.visible = false
  // Most of the slow part (Three.js download, session request) is
  // already done by the time we reach this point — most of the visible
  // loading time is the texture-decode wait. Start at a meaningful
  // baseline so the user sees motion not "stuck at 0%".
  loading.setProgress(0.6, t('vr.loading.building'))

  // Initial HUD state + texture. The loading scene stays up until the
  // texture reports readiness (for video this can take several hundred
  // ms — the forced seek decode); for images / no dataset the callback
  // fires synchronously during setTexture, BEFORE `active` is assigned
  // below. Readiness therefore only records a mark; every later step of
  // the handover runs from the render loop, where `active` exists.
  loading.setProgress(0.8, t('vr.loading.dataset'))
  let loadingFinalized = false
  /**
   * True once the loading scene has been removed + disposed — by
   * either the fade-out path or the session-end teardown path.
   * Guards against double-disposal if both fire (session ends
   * during fade-out).
   */
  let loadingDisposed = false
  /**
   * True once the splash has handed over and the real scene is on
   * screen. Gates the placement chrome: the 3D Place button and the DOM
   * Re-place button used to be live from the first frame while
   * `setEarthVisible(false)` had the globe hidden, so a phone could
   * enter Place mode and anchor a globe that was not there yet.
   */
  let sceneRevealed = false
  /**
   * Loading handover state, all driven from the render loop's own clock
   * rather than from timers or a promise chain. `loopElapsed` counts
   * XR-frame seconds since the first frame; readiness marks
   * `loadingReadyAt`; after LOADING_READY_PAUSE_S the fade starts; once
   * `loading.isFadedOut()` reads true the real scene is revealed. The
   * previous version chained two setTimeouts and a promise, and on a
   * phone the splash stayed up for the whole session — the user placed
   * and resized the atmosphere shell of a hidden globe behind it. A
   * timer-free path cannot stall that way, and the fallback makes an
   * untextured globe the worst case.
   */
  let loopElapsed = 0
  let loadingReadyAt: number | null = null
  let fadeStarted = false
  // Mirror the 2D app's viewport layout inside VR. Count=1 is the
  // backward-compatible single-globe path; count=2/4 builds the arc
  // with secondary globes. Scene slot 0 always reflects the 2D app's
  // *current* primary panel (drives the photoreal stack + HUD +
  // loading fade-out); scene slots 1..N hold the non-primary panels
  // in 2D order. VR follows the 2D app's layout in lockstep —
  // tapping a globe in VR does not, by itself, reorder panels here;
  // grab-rotate acts on every globe uniformly (see cead66d for why
  // the original tap-to-promote path was ripped out).
  const initialPanelCount = ctx.getPanelCount()
  logger.info(`[VR] Entering with ${initialPanelCount} panel(s), primary: ${ctx.getPrimaryIndex()}`)
  scene.setPanelCount(initialPanelCount)
  syncSecondaryTextures(scene, ctx, initialPanelCount)
  /** Readiness arrived (texture path) or was forced (fallback). Idempotent. */
  const finishLoading = (): void => {
    if (loadingFinalized) return
    loadingFinalized = true
    loadingReadyAt = loopElapsed
    try {
      loading.setProgress(1.0, t('vr.loading.ready'))
    } catch (err) {
      logger.warn('[VR] loading.setProgress failed:', err)
    }
  }
  /** Remove the loading scene and reveal the globe + HUD. Idempotent. */
  const revealScene = (): void => {
    if (loadingDisposed) return
    loadingDisposed = true
    scene.scene.remove(loading.group)
    loading.dispose()
    if (active) active.loading = null
    scene.setEarthVisible(true)
    // AYNI: a phone's controls are in the DOM overlay (xrPhoneChrome); the
    // 3D dock is for headsets.
    hud.mesh.visible = !phoneUi
    sceneRevealed = true
    syncPlacementChrome()
    syncMarkerChrome()
    syncPhoneChrome()
    // AYNI (Quest review E5, 2026-11-03): a phone in a room that was
    // refused the camera picture has no scan; it used to get no button and
    // no word. Place by hand still works. (After the phone's controls, so
    // the notice has somewhere to show.)
    if (
      handheldAr &&
      !markerAlign &&
      isMarkerAlignRequested() &&
      session.enabledFeatures !== undefined &&
      !session.enabledFeatures.includes('camera-access')
    ) {
      showNotice(t('vr.marker.noCameraAccess'))
    }
  }
  scene.setTexture(ctx.getDatasetTexture(), finishLoading)

  /**
   * AYNI: the date the globe shows — the 2D label's string, else (a
   * stream with a declared axis and no catalogue dates) the date track's
   * own playhead label.
   */
  const currentDateLabel = (): string | null => {
    const label = ctx.getDatasetTimeLabel()
    if (label) return label
    const timeline = ctx.getDatasetTimeline()
    return timeline ? formatPlayheadLabel(timeline.currentMs, timeline.cadenceMs) : null
  }
  /** The dock's state for this frame. */
  // AYNI: every session starts with the globe still.
  spinRate = 0
  const hudState = (): VrHudState => {
    const timeline = sceneRevealed ? ctx.getDatasetTimeline() : null
    if (notice && performance.now() > notice.until) notice = null
    return {
      datasetTitle: ctx.getDatasetTitle(),
      isPlaying: ctx.isPlaying(),
      hasVideo: ctx.hasVideoDataset(),
      isMuted: ctx.isMuted(),
      panelCount: ctx.getPanelCount(),
      primaryIndex: ctx.getPrimaryIndex(),
      browseOpen: browse.current() === 'browse',
      openPanel: browse.current(),
      dateLabel: currentDateLabel(),
      speed: tools?.playbackRate() ?? 1,
      // A stream with a declared axis has the date track; the bar is for the rest.
      progress: tools && ctx.hasVideoDataset() && !timeline ? tools.playbackProgress() : null,
      locked: tools?.isLocked() ?? false,
      notice: notice?.text ?? null,
      voice: ctx.getVoiceState?.() ?? null,
      spin: spinRate,
      buffering: ctx.isBuffering?.() ?? false,
    }
  }
  const showNotice = (text: string): void => {
    // AYNI (Quest review B2): long enough to read: 4.5 s, more for a longer notice, at most 9 s.
    notice = { text, until: performance.now() + Math.min(9000, Math.max(4500, 1500 + text.length * 55)) }
    phoneChrome?.notice(text)
  }
  hud.setState(hudState())

  // --- Optional in-view diagnostic (`?vrDebug=1`) ---
  // The phone this AR mode runs on has no devtools, so a bug report has
  // to be readable off the screen itself. Inert unless the URL asks.
  // See vrDebugPanel.ts for what each field answers.
  const debugEnabled =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('vrDebug') === '1'
  let debugPanel: VrDebugPanelHandle | null = null
  if (debugEnabled) {
    debugPanel = createVrDebugPanel(THREE_)
    scene.scene.add(debugPanel.mesh)
    logger.info('[VR] debug panel enabled (?vrDebug=1)')
  }
  let lastDebugRefreshMs = 0

  // XRControllerModelFactory was imported earlier (before scene
  // construction) so the loading-scene fade-out timing stays
  // predictable — see the comment at that import.
  const vrProjection: 'vr' | 'ar' = isAr ? 'ar' : 'vr'

  /** Compute the lat/lon under the camera's central forward ray
   * where it hits the globe, along with the current scale-derived
   * zoom and head-to-globe orientation. Returns null when the ray
   * misses the sphere (e.g. user looking away from the globe).
   * Kept inline so `scene.globe`, `camera`, and `THREE_` stay in
   * scope without threading them through another helper file. */
  function captureVrCameraState(): {
    center_lat: number
    center_lon: number
    zoom: number
    bearing: number
    pitch: number
  } | null {
    const headOrigin = new THREE_.Vector3()
    const headDir = new THREE_.Vector3(0, 0, -1)
    camera.getWorldPosition(headOrigin)
    camera.getWorldDirection(headDir)
    const ray = new THREE_.Ray(headOrigin, headDir)
    const sphereCenter = new THREE_.Vector3()
    scene.globe.getWorldPosition(sphereCenter)
    const radius = scene.globe.scale.x
    const sphere = new THREE_.Sphere(sphereCenter, radius)
    const hit = new THREE_.Vector3()
    if (!ray.intersectSphere(sphere, hit)) return null
    // Translate the hit into the globe's local frame, then undo
    // the globe's rotation so the vector represents the Earth-
    // fixed point under the user's gaze.
    const local = hit.clone().sub(sphereCenter).divideScalar(radius)
    const inverse = scene.globe.quaternion.clone().invert()
    local.applyQuaternion(inverse)
    // AYNI (2026-10-22): the place drawn there through the headset's zoom.
    const zoomState = getFocusZoom()
    const shown = displayToSource(zoomState, [local.x, local.y, local.z])
    local.set(shown[0], shown[1], shown[2])
    // Spherical coords on a unit sphere. lat = asin(y), lon =
    // atan2(x, -z) — matches MapLibre's +X east, +Y up, +Z south
    // convention used by photorealEarth.ts.
    const lat = (Math.asin(local.y) * 180) / Math.PI
    const lon = (Math.atan2(local.x, -local.z) * 180) / Math.PI
    // Derive a MapLibre-comparable zoom from the head-to-globe
    // distance. A neutral view (scale 1, viewing distance ≈ 3m)
    // maps to zoom 2 to echo the photo-realistic default. Clamped
    // to MapLibre's typical 0-20 range for dashboard parity.
    const viewDistance = headOrigin.distanceTo(sphereCenter)
    const approxZoom = Math.log2(Math.max(0.1, radius / Math.max(0.1, viewDistance))) + 4 + Math.log2(zoomState.m)
    const zoom = Math.max(0, Math.min(20, approxZoom))
    // Decompose globe rotation into bearing (yaw) + pitch. Y-axis
    // euler in world space represents how far the user has spun
    // the globe; X-axis represents tilt.
    const euler = new THREE_.Euler().setFromQuaternion(scene.globe.quaternion, 'YXZ')
    const bearing = (euler.y * 180) / Math.PI
    const pitch = (euler.x * 180) / Math.PI
    return {
      center_lat: lat,
      center_lon: lon,
      zoom,
      bearing: ((bearing % 360) + 360) % 360,
      pitch,
    }
  }

  // --- Place-mode handlers, shared by XR selects and the DOM touch
  // layer. Named closures (rather than inline in the interaction
  // context below) so the XR select path (vrInteraction) and the
  // handheld-AR touch layer (vrPlacementTouch) funnel through the
  // same confirm / cancel logic.
  let placementTouch: VrPlacementTouchHandle | null = null
  /**
   * Handheld-AR rotate layer: one finger spins the globe on its own
   * axis. Mounted only on a handheld with a granted DOM overlay, and
   * armed only OUTSIDE Place mode — the placement flow owns the globe
   * while it is active.
   */
  let rotateTouch: VrRotateTouchHandle | null = null
  /**
   * Single entry point for Place-mode transitions — keeps the
   * placement state machine and the DOM touch layer (cancel button
   * visibility, tap/drag interception) in lockstep.
   */
  const setPlacing = (placingNow: boolean): void => {
    if (!placement) return
    placement.setPlacing(placingNow)
    placementTouch?.setPlacing(placingNow)
  }
  const onPlaceButton = (): void => {
    // A placement by hand replaces the marker's: the two do not run together.
    markerAlign?.stop()
    // Toggle Place mode. Re-tap exits without placing.
    const placingNow = !(placement?.isPlacing() ?? false)
    setPlacing(placingNow)
    // AYNI (Quest review B6): a headset's steps on the dock (a phone has its own overlay).
    if (placingNow && !handheldAr) showNotice(t('vr.place.headsetPosition'))
  }
  /** A short buzz on a phone; absent on headsets and computers. */
  const buzz = (): void => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(15)
  }

  const onPlaceConfirm = (): void => {
    if (!placement) return
    // AYNI (Quest review E2, 2026-11-03): a headset places in two steps
    // (where, then how high); a phone in one, resting the globe on the
    // surface it is aimed at. A phone's height step followed its pitch,
    // so aiming down at a table put the globe under it.
    if (placement.getStep() === 'position' && !handheldAr) {
      if (!placement.getReticlePosition()) return
      buzz()
      placement.advanceStep()
      showNotice(t('vr.place.headsetHeight'))
      return
    }
    const height = placement.getStep() === 'height'
    const surface = height ? placement.getBasePosition() : placement.getReticlePosition()
    if (!surface) {
      // AYNI (Quest review E10): nothing aimed at yet; say so.
      if (handheldAr) showNotice(t('vr.phone.aimFirst'))
      return
    }
    buzz()
    const chosen = height ? placement.getPlacementPosition() : null
    const hold: GlobeHold = chosen
      ? { kind: 'height', offset: chosen.y - surface.y }
      : { kind: 'rest', gap: SURFACE_GAP_M }
    setPlacing(false)
    // AYNI (Quest review E3): in a room aligned by the marker, the sphere
    // moves and keeps the room's heading. Its outline stays on the marker.
    placedByMarker = false
    globePlacement.place(surface, hold, markerFrame ? markerFrame.orientation : null, true)
  }

  /**
   * AYNI: put the sphere on the marker. Position and heading both come
   * from the marker, so every phone that scans the same sheet shows the
   * sphere in the same place, turned the same way: floating above the
   * marker's middle, the prime meridian facing the marker's bottom edge.
   * The size is reset as well, so the phones agree on that too.
   */
  const applyMarkerFrame = (found: MarkerFrame): void => {
    setPlacing(false)
    cancelFlyTo()
    buzz()
    // AYNI (owner D3, 2026-11-03): a sphere for phones round a table,
    // 0.45 of the full size (it was a metre across).
    scene.globe.scale.setScalar(MARKER_GLOBE_SCALE)
    zoomOverlay?.setScale(MARKER_GLOBE_SCALE)
    // The sphere's own +X is longitude 0 (see flyToOnGlobe); a quarter
    // turn about the vertical brings it round to the marker's +Z, its
    // bottom edge.
    scene.globe.quaternion
      .set(found.orientation.x, found.orientation.y, found.orientation.z, found.orientation.w)
      .multiply(new THREE_.Quaternion().setFromAxisAngle(new THREE_.Vector3(0, 1, 0), -Math.PI / 2))
    const heading = new THREE_.Quaternion(
      found.orientation.x, found.orientation.y, found.orientation.z, found.orientation.w,
    )
    markerFrame = { orientation: heading }
    placedByMarker = true
    // Resting a fixed gap above the marker at any size, and anchored on
    // the frame the marker was found on (the placement's update runs after
    // the scan in the loop).
    globePlacement.place(found.position, { kind: 'rest', gap: MARKER_GAP_M }, heading, false)
  }

  /**
   * Axis progress a scrub is holding the playhead at, or null when none
   * is in flight. The drawer prefers it over the host's snapshot so the
   * strip follows the finger between the rationed seeks.
   */
  let timelineScrubProgress: number | null = null

  const interaction = createVrInteraction(THREE_, XRControllerModelFactory, {
    scene: scene.scene,
    globe: scene.globe,
    // Raycast target list: primary + every secondary. vrInteraction
    // treats them uniformly (grab any globe → rotate primary →
    // scene.update copies the quaternion to all secondaries).
    getAllGlobes: () => scene.allGlobes,
    hud,
    browse,
    khipu: khipuPanel,
    onKhipuAction: (action) => ctx.khipuAction?.(action),
    // AYNI (owner, 2026-10-11): the legend's ✕ parks it on the View page; it flies there (below).
    legend: legendPanel,
    onLegendAction: () => tools?.setLegendParked(true),
    timeline,
    placement,
    renderer,
    // Handheld session: every XR path that can write the globe's scale
    // (two-hand pinch, thumbstick zoom) stands down for the whole
    // session. A constant — it does not flap with the input sources.
    isScreenInput: () => handheldAr,
    focusZoomCeiling: () => focusZoomView.ceiling(),
    // The rotate layer owning the globe's rotation is the narrower
    // fact, and it is what stops vrInteraction grabbing the globe.
    domTouchActive: () => rotateTouchMounted,
    onCarryStart: () => {
      globePlacement.release()
      placedByMarker = false
    },
    onCameraSettled: () => {
      const state = captureVrCameraState()
      if (!state) return
      emitCameraSettled({
        slot_index: '0',
        projection: vrProjection,
        layer_id: ctx.getDatasetId() ?? '',
        ...state,
      })
    },
    // AYNI: every press on the side panel; the panel handles its own
    // tabs, filters and pages and hands the rest to the host's tools.
    onBrowseAction: (action) => browse.act(action),
    onHudAction: (action, uv) => {
      if (action === 'play-pause') {
        ctx.togglePlayPause()
      } else if (action === 'mute') {
        // Flip the primary video's muted flag. The per-frame
        // hud.setState pipes ctx.isMuted() back through so the
        // icon updates on the next redraw.
        ctx.toggleMute()
      } else if (action === 'step-back' || action === 'step-forward') {
        tools?.stepFrame(action === 'step-back' ? -1 : 1)
      } else if (action === 'speed') {
        if (tools) tools.setPlaybackRate(nextSpeed(tools.playbackRate()))
      } else if (action === 'spin') {
        // AYNI (owner, 2026-10-22): the auto-rotate, round its speeds; the
        // per-frame hud.setState shows the new rate on the button.
        spinRate = nextSpinRate(spinRate)
      } else if (action === 'seek') {
        const fraction = hud.seekFraction(uv)
        if (fraction !== null) tools?.seekProgress(fraction)
      } else if (action === 'browse' || action === 'layers' || action === 'data' || action === 'view' || action === 'help') {
        // AYNI: the dock's panel buttons; a second press closes the page.
        browse.toggle(action)
      } else if (action === 'voice') {
        // Orbit's mic. Called straight from the select handler rather
        // than deferred, so the tap is as close to a user gesture as
        // the session allows — starting capture and unlocking speech
        // output can both depend on one.
        ctx.toggleVoice?.()
      } else if (action === 'exit-vr') {
        // Programmatic exit — fires the 'end' event, which routes
        // through the same teardown path as headset-initiated exits.
        void session.end().catch(err =>
          logger.warn('[VR] session.end() from exit-vr button failed:', err),
        )
      }
    },
    onPlaceButton,
    onPlaceConfirm,
    onGlobeTap: (uv) => {
      const { lat, lon } = sphereUvToLatLon(uv)
      // AYNI (satellites plan): while the sky asks where the viewer is, a tap on the globe answers.
      if (satelliteSky?.onGlobePlace(lat, lon)) return
      ctx.onGlobeTap?.(lat, lon, handheldAr ? 'touch' : 'select')
    },
    // AYNI: A / X on a controller drops a pin where it aims.
    // AYNI (satellites plan): a tap that aims at a satellite selects it (again: lets it go).
    // AYNI (owner, 2026-10-24, Fully VR): a trigger on the empty sky turns the sky, and the globe with it.
    canGrabSky: fullVrCoupled,
    onRayTap: (origin, direction) => {
      if (satelliteLayer?.skyOn && satelliteSky) return satelliteSky.onRayTap(origin, direction, camera)
      if (!satelliteLayer?.on || !satellites) return false
      const i = satellites.pick(origin, direction, handheldAr)
      if (i === null) return false
      satelliteLayer.select(satelliteLayer.selected === i ? null : i)
      return true
    },
    onGlobePin: (uv) => {
      const { lat, lon } = sphereUvToLatLon(uv)
      ctx.onGlobeTap?.(lat, lon, 'pin')
    },
    onTimelineSeek: (progress, phase) => {
      const snapshot = ctx.getDatasetTimeline()
      if (!snapshot) return
      // The strip follows the finger immediately; the decoder is steered
      // through the host, which maps progress to the frame's midpoint.
      timelineScrubProgress = phase === 'preview' ? progress : null
      ctx.seekToTimelineDate(
        snapshot.startMs + progress * (snapshot.endMs - snapshot.startMs),
      )
    },
  })

  // --- Handheld-AR placement chrome ---
  // Mounted only when the session actually granted the DOM overlay
  // (Android `screen` input) AND the session is a handheld: touches on
  // empty screen then arrive as DOM touch events, which buys the
  // explicit Place / Cancel buttons, the Re-place corner button, and the
  // `beforexrselect` dedup that swallows stray taps during placement so
  // they never reach the XR confirm short-circuit. Controller sessions
  // keep trigger-to-confirm + the raycast Place button. See
  // vrPlacementTouch.ts for the full rationale.
  //
  // Idempotent and deferred: both affordances appear only once the
  // loading handover has revealed the globe (see `sceneRevealed`), so
  // this is called from `revealScene()` and no-ops if it runs earlier.
  function syncPlacementChrome(): void {
    if (!placement || !sceneRevealed) return
    // AYNI: a phone's Place is on its dock, not a disc in the scene.
    if (placementAvailable && !phoneUi) placement.placeButtonMesh.visible = true
    if (placementTouch || !handheldAr || !domOverlayActive || !domOverlayRoot) return
    placementTouch = createVrPlacementTouch({
      onConfirm: onPlaceConfirm,
      onCancel: () => setPlacing(false),
      onRePlace: onPlaceButton,
      // AYNI: Place is on the phone's dock now; no corner button for it.
      showReplace: !phoneUi,
    })
    placementTouch.mount(domOverlayRoot)
  }
  syncPlacementChrome()

  // AYNI: the marker scan's button, beside the placement chrome and
  // revealed with it. Only where the scan exists (see markerAlign).
  function syncMarkerChrome(): void {
    if (!markerAlign || markerTouch || !sceneRevealed) return
    if (!handheldAr || !domOverlayActive || !domOverlayRoot) return
    markerTouch = createVrMarkerAlignTouch({
      onToggle: () => {
        if (markerAlign.isScanning()) {
          markerAlign.stop()
        } else {
          setPlacing(false)
          markerAlign.start()
        }
      },
    })
    markerTouch.mount(domOverlayRoot)
    // AYNI (Quest review E5, 2026-11-03): in a room the scan starts by
    // itself: lining up with the room is the first thing a phone there
    // needs, and the button only stops it.
    if (joinedRoomCode() !== null && !markerAlign.isScanning()) markerAlign.start()
  }

  // AYNI (the AR refresh): a phone's controls over the camera picture —
  // top line, legend, transport and dock (src/ui/xrPhoneChrome.ts).
  // Revealed with the scene, like the placement chrome.
  let phoneChrome: XrPhoneChromeHandle | null = null
  /** Where the primary dataset is along its track, 0..1: the declared axis, else the video. */
  const trackPosition = (): number | null => {
    const timeline = ctx.getDatasetTimeline()
    if (timeline && timeline.endMs > timeline.startMs) {
      return Math.max(0, Math.min(1, (timeline.currentMs - timeline.startMs) / (timeline.endMs - timeline.startMs)))
    }
    return tools?.playbackProgress() ?? null
  }
  function syncPhoneChrome(): void {
    if (!phoneUi || phoneChrome || !sceneRevealed || !domOverlayRoot) return
    phoneChrome = createXrPhoneChrome({
      title: () => ctx.getDatasetTitle(),
      dateLabel: currentDateLabel,
      isLive: () => tools?.isLive() ?? false,
      hasVideo: () => ctx.hasVideoDataset(),
      isPlaying: () => ctx.isPlaying(),
      isMuted: () => ctx.isMuted(),
      togglePlay: () => ctx.togglePlayPause(),
      step: (direction) => tools?.stepFrame(direction),
      toggleMute: () => ctx.toggleMute(),
      speed: () => tools?.playbackRate() ?? 1,
      setSpeed: (speed) => tools?.setPlaybackRate(speed),
      position: trackPosition,
      trackLabels: () => tools?.trackLabels() ?? null,
      seek: (fraction) => {
        const timeline = ctx.getDatasetTimeline()
        if (timeline) ctx.seekToTimelineDate(timeline.startMs + fraction * (timeline.endMs - timeline.startMs))
        else tools?.seekProgress(fraction)
      },
      locked: () => tools?.isLocked() ?? false,
      legendKey: () => tools?.legendKey() ?? '',
      legend: () => tools?.legendElement() ?? null,
      parkLegend: () => tools?.setLegendParked(true),
      isPlacing: () => placement?.isPlacing() ?? false,
      following: () => followedGlobe !== null,
      place: () => {
        if (placementAvailable) onPlaceButton()
        else showNotice(t('vr.phone.noPlace'))
      },
      exit: () => {
        void session.end().catch((err) => logger.warn('[VR] session.end() from the phone controls failed:', err))
      },
      openBrowse: () => tools?.openBrowse(),
      closeBrowse: () => tools?.closeBrowse(),
      overlays: () => tools?.rtPlaying() ?? [],
      setOverlayHidden: (id, hidden) => tools?.setRtHidden(id, hidden),
      closeOverlay: (id) => tools?.toggleRt(id),
      moveOverlay: (id, to) => tools?.moveRt(id, to),
      overlayColours: (id) => tools?.rtColours(id) ?? null,
      setOverlayDisplay: (id, display) => tools?.setRtDisplay(id, display),
      setOverlayOpacity: (id, opacity) => tools?.setRtOpacity(id, opacity),
      base: () => {
        const look = tools?.baseLook() ?? null
        if (!look) return null
        const colours = tools?.baseColours() ?? null
        return { ...look, colours, coloursReason: colours?.scale ? undefined : t(colours?.classified ? 'palette.dialog.classes' : 'palette.dialog.picture', { title: look.title }) }
      },
      setBaseDisplay: (display) => tools?.setBaseDisplay(display),
      setBaseOpacity: (opacity) => tools?.setBaseOpacity(opacity),
      setBaseHidden: (hidden) => tools?.setBaseHidden(hidden),
      closeBase: () => tools?.closeBase(),
    })
    phoneChrome.mount(domOverlayRoot)
  }

  // --- Handheld-AR zoom slider ---
  // The one sizing control a phone has: an explicit DOM slider, never a
  // touch gesture. Mounted once, INTO the overlay root so the browser
  // renders it over the camera feed; headsets keep the thumbstick.
  let zoomOverlay: VrZoomOverlayHandle | null = null
  const syncZoomOverlay = (): void => {
    const wantOverlay = handheldAr && domOverlayActive
    if (wantOverlay && !zoomOverlay) {
      zoomOverlay = createVrZoomOverlay({
        onZoom: (raw) => {
          const clamped = Math.max(MIN_GLOBE_SCALE, Math.min(globeScaleCeiling(), raw))
          scene.globe.scale.setScalar(clamped)
          // A follower's slider stops where the globe does.
          if (clamped < raw) zoomOverlay?.setScale(clamped)
        },
        initialScale: scene.globe.scale.x,
        minScale: MIN_GLOBE_SCALE,
        maxScale: MAX_GLOBE_SCALE,
      })
      zoomOverlay.mount(domOverlayRoot ?? document.body)
    } else if (!wantOverlay && zoomOverlay) {
      zoomOverlay.dispose()
      zoomOverlay = null
    }
  }
  syncZoomOverlay()

  // --- Handheld-AR rotate (one finger, nothing else) ---
  // The whole of what touch does to a placed globe on a phone: a
  // sideways drag spins it about its own axis. No move, no pinch, no
  // twist — a second finger is ignored, and no touch here can write the
  // scale or the position. See src/ui/vrRotateTouch.ts, and
  // vrInteraction's isScreenInput / domTouchActive guards for how the
  // XR grab, pinch and thumbstick paths stand down on this device.
  if (handheldAr && domOverlayActive) {
    logger.info('[VR] handheld rotate layer mounted — one finger spins and tilts the globe')
    rotateTouch = createVrRotateTouch({
      onRotate: (delta) => {
        // Object3D.rotateY is about the globe's LOCAL Y — its own
        // axis, whatever orientation it currently has. scene.update
        // copies the quaternion to any secondary globes.
        scene.globe.rotateY(delta)
      },
      // AYNI: the same drag's vertical travel tilts the globe toward or
      // away from the viewer, bounded short of upside down (vrGlobeTilt).
      onTilt: (delta) => {
        tiltGlobe(THREE_, scene.globe, camera, delta)
      },
    })
    rotateTouchMounted = true
  }

  active = {
    session,
    renderer,
    camera,
    scene,
    hud,
    disposeZoomOverlay: () => {
      zoomOverlay?.dispose()
      zoomOverlay = null
      rotateTouch?.dispose()
      rotateTouch = null
      rotateTouchMounted = false
      placementTouch?.dispose()
      placementTouch = null
      markerTouch?.dispose()
      markerTouch = null
      // AYNI: hands the borrowed Browse and Tools panels back to the page.
      phoneChrome?.dispose()
      phoneChrome = null
    },
    browse,
    geoMedia,
    vectorLayers: new Map(),
    satellites,
    satelliteSky,
    worldPanels,
    khipu: khipuPanel,
    timeline,
    timeLabel,
    interaction,
    loading,
    placement,
    refSpace: placementRefSpace,
    hitTestSource,
  }

  notifyActive = showNotice

  // --- Render loop ---
  //
  // AYNI (the AR refresh, 2026-10-04): the dock, the date track, the side
  // panel, the legend, the Khipu panel and the Place button are laid out
  // around the globe every frame from its CURRENT size and from the side
  // the viewer is on (`globeLayout`). The first version used fixed world
  // offsets, so a globe zoomed past about 1.3× swallowed the HUD and the
  // date, and walking round a placed globe left every panel on its far
  // side. The catalogue the side panel needs is polled by the panel
  // itself, once a second while Browse is open.
  /** Air between the globe's lowest point and the dock's top edge, metres. */
  const DOCK_GAP = 0.06
  /** The dock never sinks further below the eyes than this, however big the globe. */
  const DOCK_MAX_BELOW_EYES = 0.8
  /** Air between the globe's side and a side panel, metres. */
  const SIDE_GAP = 0.08
  const TRACK_HEIGHT = 0.11
  const LEGEND_WIDTH = 0.2
  /** AYNI: the legend's flight between its place and the side panel (or the dock), ms. */
  const LEGEND_FLY_MS = 420
  /** Parked at the last read (null before the first), and the flight under way. */
  let legendWasParked: boolean | null = null
  let legendFly: { parking: boolean; start: number } | null = null
  const legendHome = new THREE_.Vector3()
  const worldUp = new THREE_.Vector3(0, 1, 0)
  /** Horizontal unit vector from the globe toward the viewer, and the viewer's right. */
  const scratchFwd = new THREE_.Vector3()
  const scratchRight = new THREE_.Vector3()
  /** Throttle for host reads that need not run at frame rate (legend, pins, a phone's controls). */
  let lastToolsPollMs = -Infinity
  /** Scratch reused per-frame for position math; avoids GC churn. */
  const scratchPos = new THREE_.Vector3()
  /** AYNI: scratch for the followed sphere's target orientation. */
  const followTarget = new THREE_.Quaternion()
  /** Scratch vector reused every frame by the billboard-lookAt block below. */
  const scratchCamPos = new THREE_.Vector3()
  /** Headset forward direction, reused by the VR gaze + height placement path. */
  const scratchGazeDir = new THREE_.Vector3()
  /** AYNI (Quest review E6): the size the slider last showed for a followed globe. */
  let sliderScaleShown = Number.NaN
  // `lastTime` starts null so the very first frame uses its own
  // timestamp as "previous" and computes a 0-duration delta —
  // rather than mixing XR's frame timestamp (first callback arg)
  // with performance.now() from pre-loop init, which can be on a
  // different clock and produce a huge first delta.
  let lastTime: number | null = null
  /**
   * One-shot gaze-based spawn (both modes). The hardcoded GLOBE_POSITION
   * assumes the user faces -Z at session start; instead, on the first
   * frame with a valid viewer pose we place the globe in front of
   * wherever they're actually looking (see vrSpawn.ts). In AR the
   * globe floats mid-air at eye height until placed — same as today's
   * hardcoded spawn — and a restored placement anchor still wins: the
   * per-frame anchor sync below overwrites this position as soon as
   * the anchor pose resolves. The HUD / Place button re-anchor to the
   * globe every frame below, and secondary globes follow the primary
   * in scene.update(), so no other object needs an explicit respawn.
   */
  let pendingGazeSpawn = true
  renderer.setAnimationLoop((time, frame) => {
    // `??` instead of `||` — some XR implementations emit a valid
    // timestamp of 0 on the very first frame, which `||` would
    // falsely treat as "no time" and fall back to performance.now(),
    // mixing clocks.
    const now = time ?? performance.now()
    const previousTime = lastTime ?? now
    const delta = Math.min(0.1, (now - previousTime) / 1000)
    lastTime = now

    if (!active) return
    sessionTelemetry.frames++

    // Gaze-based spawn — runs once, on the first frame where the
    // viewer pose is available. `renderer.xr.getReferenceSpace()` is
    // the space Three.js resolves all camera/scene coords in, so the
    // pose and globe.position share a frame regardless of whether the
    // session got local-floor or the local fallback.
    if (pendingGazeSpawn && frame) {
      const refSpace = renderer.xr.getReferenceSpace()
      const pose = refSpace ? frame.getViewerPose(refSpace) : null
      if (pose) {
        pendingGazeSpawn = false
        const spawn = computeGazeSpawnPosition(
          pose.transform.position,
          pose.transform.orientation,
        )
        active.scene.globe.position.set(spawn.x, spawn.y, spawn.z)
      }
    }

    // Spatial placement — per-frame work, but only while in Place mode.
    // Two position sources, one height step:
    //   - AR + hit-test  → update() projects the controller reticle.
    //   - VR (no geometry) → updateGaze() projects the headset ray
    //     onto a virtual floor.
    //   - height step (either mode) → updateHeight() maps headset
    //     pitch to a vertical position along the rail.
    if (active.placement) {
      if (active.refSpace && frame && active.placement.getStep() === 'position') {
        active.placement.update(frame, active.refSpace)
      }
      // VR gaze position step — no XR frame/refSpace needed, the
      // Three.js camera already carries the headset world pose.
      if (!isAr && active.placement.getStep() === 'position') {
        active.camera.getWorldPosition(scratchCamPos)
        active.camera.getWorldDirection(scratchGazeDir)
        active.placement.updateGaze(scratchCamPos, scratchGazeDir)
      }
      // Height step — every device, one input: the pose's own pitch.
      // On a headset that is the user's head; on a phone it is the phone
      // being aimed with, which is the same gesture the position step's
      // reticle already follows. (The handheld layer used to own a touch
      // drag here too; touch is rotate-only now.) Drive the globe LIVE
      // along the chosen height so the user sees it slide as they tilt,
      // instead of a frozen globe that only jumps on confirm — which
      // read as "nothing locked". The chosen XZ is frozen from the
      // position step; only Y moves.
      if (active.placement.getStep() === 'height') {
        active.camera.getWorldDirection(scratchGazeDir)
        const elevation = Math.asin(
          Math.max(-1, Math.min(1, scratchGazeDir.y)),
        )
        // AYNI (Quest review E2): never under the surface the first step chose.
        const base = active.placement.getBasePosition()
        active.placement.updateHeight(
          elevation,
          base ? lowestCentreAbove(base.y, GLOBE_RADIUS * active.scene.globe.scale.x) : undefined,
        )
        const preview = active.placement.getPlacementPosition()
        if (preview) {
          active.scene.globe.position.copy(preview)
        }
      }
    }

    // AYNI: marker scan. Before the anchor sync, so the frame a marker
    // is found on already shows the sphere above it.
    if (markerAlign) {
      if (markerAlign.isScanning() && frame && active.refSpace && active.hitTestSource) {
        const found = markerAlign.update(frame, active.refSpace, active.hitTestSource, now)
        if (found) {
          applyMarkerFrame(found)
          markerOutline?.show(found.size, found.orientation, now)
        }
      }
      markerTouch?.setHidden(active.placement?.isPlacing() ?? false)
      markerTouch?.setState(markerAlign.isScanning(), markerAlign.getStatus())
    }

    // Arm the rotate layer only when nothing else owns the touch: the
    // placement flow owns the globe while it is active, and an open
    // browse panel owns a drag as a list scroll (that scroll runs
    // through the XR ray, so without this a drag across the panel would
    // scroll the list AND spin the globe). It also waits for the splash
    // to hand over — there is nothing placed, or even visible, to spin
    // before that. `setEnabled` is a no-op when the state has not
    // changed.
    rotateTouch?.setEnabled(
      sceneRevealed &&
        !active.placement?.isPlacing() &&
        !active.browse.isVisible() &&
        // AYNI: a phone's sheet (Browse, Layers, Data, View, Help) owns the touch.
        !(phoneChrome?.sheetOpen() ?? false) &&
        // AYNI (Quest review E6): a follower's globe is the lead's to turn.
        !followedGlobe,
    )

    // Sync globe position from the system-tracked anchor, if any.
    // The anchor's anchorSpace is resolved in local-floor coords
    // each frame — but critically, the system adjusts what
    // "(anchor.x, anchor.y, anchor.z)" means as local-floor gets
    // re-based, so the globe stays bolted to the real surface.
    // AYNI (Quest review E1, E2, E4, B7, 2026-11-03): vrGlobePlacement
    // makes the anchor a placement asked for, holds the globe's height
    // for its size, turns it with the marker's refined heading, and lets a
    // restored globe found out of reach go. After the marker scan, so a
    // marker's anchor is made on the frame it was found on; not while
    // placing, when the placement flow owns the globe.
    if (!active.placement?.isPlacing()) {
      globePlacement.update(frame, active.refSpace)
    }
    // AYNI: the scan's outline rides with the anchor, on the marker under
    // the sphere; a placement by hand since removes it.
    if (markerOutline?.isShown()) {
      const g = active.scene.globe.position
      markerOutline.update(
        placedByMarker ? { x: g.x, y: g.y - globePlacement.offset(), z: g.z } : null,
        now,
      )
    }

    // Swap the dataset texture if the app loaded/changed something
    // while we're in VR. The scene's setTexture is internally
    // debounced (compares against its own active key) so polling
    // every frame is cheap in the steady state. Also mirror the
    // 2D viewport's panel count + per-slot textures — setPanelCount
    // is idempotent when the count hasn't changed, and per-slot
    // setSlotTexture shares the same debounce path as the primary.
    const panelCount = ctx.getPanelCount()
    active.scene.setPanelCount(panelCount)
    active.scene.setTexture(ctx.getDatasetTexture())
    // AYNI (owner, 2026-10-12): the dataset's transparency and eye, every frame (a uniform: cheap).
    const look = ctx.getDatasetLook?.()
    active.scene.setDatasetUserOpacity(look ? (look.hidden ? 0 : look.opacity) : 1)
    syncSecondaryTextures(active.scene, ctx, panelCount)
    // AYNI: the layer stack follows the 2D app's, applied on change only.
    const mapLayers = ctx.getMapLayerImages?.() ?? null
    if (mapLayers !== appliedMapLayers) {
      appliedMapLayers = mapLayers
      active.scene.setMapLayers(mapLayers)
    }
    // AYNI: the geo-media dots and panel follow the host's state the same way.
    const geoState = ctx.getGeoMedia?.() ?? null
    if (geoState !== appliedGeoMedia) {
      appliedGeoMedia = geoState
      active.geoMedia.set(geoState)
    }

    // AYNI (satellites plan): the satellites move on their own clock; the
    // selected one's card stands beside the globe, its numbers once a second.
    if (satelliteLayer && active.satellites) {
      satelliteLayer.tick()
      active.satellites.update(active.camera, renderer.getDrawingBufferSize(satelliteViewport).y)
      if (active.satelliteSky) {
        if (satelliteLayer.northRequest !== northAsked) {
          northAsked = satelliteLayer.northRequest
          active.satelliteSky.resetNorth()
        }
        active.satelliteSky.update(active.camera, satelliteViewport.y)
      }
      const sel = satelliteLayer.on ? satelliteLayer.selected : null
      const nowMs = performance.now()
      if (sel !== shownSatellite || (sel !== null && nowMs - satelliteCardAt > 1000)) {
        shownSatellite = sel
        satelliteCardAt = nowMs
        const text = sel === null ? null : satelliteFactsText(satelliteLayer, sel)
        if (text) active.worldPanels.showText(SATELLITE_CARD_ID, text, SATELLITE_CARD_PLACE)
        else active.worldPanels.hide(SATELLITE_CARD_ID)
      }
    }

    // AYNI (DSA 1.8): each globe's vector layers, at the point of their
    // animations the browser's player has them; a feature's panel beside it.
    syncVrVectorLayers(THREE_, active, ctx, panelCount)
    const inspection = ctx.getVectorInspection?.() ?? null
    if ((inspection?.key ?? null) !== shownVectorInspection) {
      shownVectorInspection = inspection?.key ?? null
      if (inspection) active.worldPanels.showText(VECTOR_INSPECT_ID, inspection.text, VECTOR_INSPECT_PLACE)
      else active.worldPanels.hide(VECTOR_INSPECT_ID)
    }

    // AYNI: the side panel polls the host (the catalogue once a second
    // while Browse is open) and redraws only on change.
    active.browse.update(performance.now())

    // The dock reflects the latest app state every frame. setState is
    // internally debounced — it only redraws when a field it draws changes.
    active.hud.setState(hudState())

    // AYNI: the legend, the pins and a phone's controls follow the host a
    // few times a second; each applies only what changed.
    const wallNow = performance.now()
    if (wallNow - lastToolsPollMs >= 200) {
      lastToolsPollMs = wallNow
      const legend = sceneRevealed && !phoneUi ? (tools?.legend() ?? null) : null
      // AYNI: parked or brought back (here, or in the browser), it flies between its place and the side panel.
      const parked = !!tools?.legendParked()
      if (legendWasParked !== null && parked !== legendWasParked && legend) legendFly = { parking: parked, start: wallNow }
      legendWasParked = parked
      legendPanel.set(legend && (!parked || legendFly?.parking) ? legend : null)
      pinMarks.set(sceneRevealed ? (tools?.pinMarks() ?? null) : null)
      phoneChrome?.update()
    }

    // AYNI: the value under the ray, beside the laser dot (headsets only;
    // a phone reads values with pins). The probe keeps its 10 Hz ration.
    if (!handheldAr && sceneRevealed) {
      const value = readVrProbe(active.interaction, ctx, now)
      const hover = value ? active.interaction.globeHover() : null
      if (value && hover) {
        const { lat, lon } = sphereUvToLatLon(hover.uv)
        readout.set(value, formatLatLon(lat, lon))
        active.camera.getWorldPosition(scratchCamPos)
        readout.place(hover.point, scratchCamPos)
      } else {
        readout.set(null, null)
      }
    }

    // AYNI: the Khipu panel follows the playing Khipu (a phone has the page's own panel in its overlay).
    active.khipu.setState(sceneRevealed && !phoneUi ? (ctx.getKhipu?.() ?? null) : null)

    // AYNI (the AR refresh): the date moved into the dock's title row (a
    // phone's top line), so the floating label above the globe stays
    // hidden — it said the same thing a hand's width away.
    active.timeLabel.setText(null)

    // Borders overlay mirrors the shared view preference. 2D toggles
    // (Tools menu, Layers) write to the same
    // preference, so the VR globe stays in sync without a dedicated
    // callback. `scene.setBordersVisible` is internally idempotent.
    active.scene.setBordersVisible(getBordersVisible())

    active.interaction.update(delta)

    // AYNI (owner, 2026-10-22): the dock's Spin — the globe turns about its
    // own axis, eastward as the Earth does, after the viewer's own inputs
    // (a trigger holding it pauses the spin, not the choice) and before
    // the fly-to and the lead, which win while they run. Zoomed in, it
    // waits until 1×, as the clouds do. scene.update carries the turn to
    // the other globes.
    if (spinRate > 0 && !pendingFlyTo && !followedGlobe && !active.interaction.isGrabbing() && !isZoomed(getFocusZoom())) {
      active.scene.globe.rotateY(spinRadians(spinRate, delta))
    }

    // flyTo animation — drives the globe quaternion toward the
    // captured target each frame. Must run AFTER interaction.update
    // (which writes user-grab rotations) and BEFORE scene.update
    // (which propagates the primary's quaternion to secondaries).
    // A fresh user grab mid-animation will overwrite globe.quaternion,
    // but the very next frame slerp reads `startQuat` not the current
    // quat — so flyTo "wins" until the duration elapses. Good enough
    // for v1; a user-grab interrupt is cheap follow-up if requested.
    if (pendingFlyTo) {
      const elapsed = now - pendingFlyTo.startTime
      // Guard the instant-jump case: durationMs = 0 (from
      // an instant camera move) would divide by zero and on
      // the first frame where elapsed === 0 produce NaN, leaving
      // the slerp in an undefined state. Collapse to t=1 so the
      // snap completes on the first tick.
      const t = pendingFlyTo.durationMs > 0
        ? Math.min(1, elapsed / pendingFlyTo.durationMs)
        : 1
      // Ease-in-out cubic — matches MapLibre flyTo's perceived pacing.
      const eased = t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2
      active.scene.globe.quaternion.slerpQuaternions(
        pendingFlyTo.startQuat,
        pendingFlyTo.endQuat,
        eased,
      )
      if (t >= 1) {
        const done = pendingFlyTo
        pendingFlyTo = null
        done.resolve()
      }
    }

    // AYNI: following a shared session's lead. After this device's own
    // inputs, so the lead's pose wins, and eased so ten messages a second
    // read as motion. An orientation given in the marker's frame is
    // brought into this session through this device's own scan of the
    // same marker; without one, it is applied as it stands.
    // AYNI (Quest review D4): a follower's own sticks and grips stop at its ceiling too.
    const ceiling = globeScaleCeiling()
    if (active.scene.globe.scale.x > ceiling) active.scene.globe.scale.setScalar(ceiling)
    if (followedGlobe) {
      const g = followedGlobe
      followTarget.set(g.q[0], g.q[1], g.q[2], g.q[3])
      if (g.aligned && markerFrame) followTarget.premultiply(markerFrame.orientation)
      active.scene.globe.quaternion.slerp(followTarget, 0.3)
      const scale = active.scene.globe.scale.x
      const nextScale = scale + (Math.max(MIN_GLOBE_SCALE, Math.min(MAX_GLOBE_SCALE, g.scale)) - scale) * 0.3
      active.scene.globe.scale.setScalar(nextScale)
      // AYNI (2026-10-22): and the lead's zoom, as it stands (their globe's turn is eased above).
      const leadZoom = g.zoom ? { m: g.zoom.m, fd: g.zoom.f, c: g.zoom.c } : plainZoom()
      if (!sameZoom(leadZoom, getFocusZoom())) setFocusZoom(leadZoom)
    }
    // AYNI (Quest review E6, 2026-11-03): a phone that follows shows the
    // lead's size on its slider, which stands down meanwhile.
    if (zoomOverlay) {
      zoomOverlay.setDisabled(followedGlobe !== null)
      const shown = active.scene.globe.scale.x
      if (followedGlobe && Math.abs(shown - sliderScaleShown) > 0.005) {
        sliderScaleShown = shown
        zoomOverlay.setScale(shown)
      }
    }

    // AYNI (owner, 2026-10-22): the right stick's zoom — its ceiling, the
    // close-up tiles, what hides while it is on, its card.
    focusZoomView.update(active.camera, now, delta, fineDatasetShown(ctx.getDatasetTexture()))

    // Scene-level per-frame sync (e.g. ground shadow scale matching
    // globe zoom). Cheap and always runs even when the loading
    // scene is still up so the shadow is correct the moment the
    // globe becomes visible.
    active.scene.update()
    // AYNI: the pins' labels lean toward the viewer from where the globe now is.
    pinMarks.update(active.camera)

    // AYNI (the AR refresh, 2026-10-04): everything around the globe is
    // placed from its CURRENT radius and from the viewer's side of it,
    // and faces the viewer. Deliberately not parented to the globe, which
    // would turn the panels with every grab.
    //   - the dock: under the globe, toward the viewer, never further than
    //     DOCK_MAX_BELOW_EYES under the eyes however big the globe grows;
    //   - under it the date track;
    //   - the side panel to the viewer's right, the legend to the left;
    //   - the 3D Place button beside the dock.
    active.camera.getWorldPosition(scratchCamPos)
    const globePos = active.scene.globe.position
    const radius = GLOBE_RADIUS * active.scene.globe.scale.x
    scratchFwd.set(scratchCamPos.x - globePos.x, 0, scratchCamPos.z - globePos.z)
    if (scratchFwd.lengthSq() < 1e-6) scratchFwd.set(0, 0, 1)
    scratchFwd.normalize()
    // The viewer looks along -fwd, so their right is up × fwd.
    scratchRight.crossVectors(worldUp, scratchFwd)
    const front = Math.min(radius * 0.6, 0.45) + 0.12
    const dockY = Math.max(globePos.y - radius - DOCK_GAP - HUD_HEIGHT / 2, scratchCamPos.y - DOCK_MAX_BELOW_EYES)
    scratchPos.set(globePos.x, dockY, globePos.z).addScaledVector(scratchFwd, front)
    active.hud.mesh.position.copy(scratchPos)
    active.hud.mesh.lookAt(scratchCamPos)
    let below = dockY - HUD_HEIGHT / 2 - 0.02

    // Date track: asked for every frame, which is what makes the host's
    // answer a cheap map read rather than a request. While a scrub is in
    // flight the drawn playhead follows the finger rather than the video
    // — the decoder is steered in steps (see vrInteraction's
    // SCRUB_SEEK_INTERVAL_MS) and the strip should not stutter with it.
    {
      // Gated on the handover like the placement chrome: the axis is a
      // description of the globe, and floating it over the splash would
      // describe a globe that is not on screen yet. A phone has its own
      // track in the overlay.
      const snapshot = sceneRevealed && !phoneUi ? ctx.getDatasetTimeline() : null
      if (snapshot) {
        const currentMs =
          timelineScrubProgress === null
            ? snapshot.currentMs
            : snapshot.startMs +
              timelineScrubProgress * (snapshot.endMs - snapshot.startMs)
        active.timeline.setState({
          ...snapshot,
          currentMs,
          scrubbing: timelineScrubProgress !== null,
        })
        scratchPos.set(globePos.x, below - TRACK_HEIGHT / 2, globePos.z).addScaledVector(scratchFwd, front)
        active.timeline.mesh.position.copy(scratchPos)
        active.timeline.mesh.lookAt(scratchCamPos)
        below -= TRACK_HEIGHT + 0.02
      } else {
        active.timeline.setState(null)
      }
    }
    if (active.browse.isVisible()) {
      scratchPos.copy(globePos)
        .addScaledVector(scratchRight, radius + SIDE_GAP + SIDE_PANEL_WIDTH / 2)
        .addScaledVector(scratchFwd, Math.min(radius * 0.4, 0.3))
      scratchPos.y = Math.max(globePos.y, dockY + HUD_HEIGHT / 2 + SIDE_PANEL_HEIGHT / 2 + 0.03)
      active.browse.mesh.position.copy(scratchPos)
      active.browse.mesh.lookAt(scratchCamPos)
    }
    // AYNI: the Khipu panel to the viewer's left, above the legend.
    if (active.khipu.isVisible()) {
      scratchPos.copy(globePos)
        .addScaledVector(scratchRight, -(radius + 0.05 + KHIPU_PANEL_WIDTH / 2))
        .addScaledVector(scratchFwd, Math.min(radius * 0.4, 0.3))
      scratchPos.y = globePos.y + Math.max(radius * 0.35, 0.12) + KHIPU_PANEL_HEIGHT / 2
      active.khipu.mesh.position.copy(scratchPos)
      active.khipu.mesh.lookAt(scratchCamPos)
    }
    active.worldPanels.update(globePos, scratchCamPos, active.scene.globe.scale.x)
    if (legendPanel.mesh.visible) {
      scratchPos.copy(globePos)
        .addScaledVector(scratchRight, -(radius + 0.05 + LEGEND_WIDTH / 2))
        .addScaledVector(scratchFwd, Math.min(radius * 0.4, 0.3))
      // AYNI: on its way to the side panel (or the dock, when no panel is open) or back, shrinking as it goes.
      if (legendFly) {
        const k = Math.min(1, (performance.now() - legendFly.start) / LEGEND_FLY_MS)
        const eased = 1 - Math.pow(1 - k, 3)
        const away = legendFly.parking ? eased : 1 - eased
        const toward = active.browse.isVisible() ? active.browse.mesh.position : active.hud.mesh.position
        legendHome.copy(scratchPos)
        scratchPos.lerpVectors(legendHome, toward, away)
        legendPanel.mesh.scale.setScalar(1 - 0.85 * away)
        if (k >= 1) {
          if (legendFly.parking) legendPanel.set(null)
          legendFly = null
          legendPanel.mesh.scale.setScalar(1)
        }
      }
      legendPanel.mesh.position.copy(scratchPos)
      legendPanel.mesh.lookAt(scratchCamPos)
    }
    if (active.placement) {
      scratchPos.set(globePos.x, dockY, globePos.z)
        .addScaledVector(scratchFwd, front)
        .addScaledVector(scratchRight, -(HUD_WIDTH / 2 + 0.09))
      active.placement.placeButtonMesh.position.copy(scratchPos)
      active.placement.placeButtonMesh.lookAt(scratchCamPos)
    }

    // Loading handover, on the loop's own clock (see the state block
    // above finishLoading for why no timers are involved):
    //   1. readiness never arrives → force it after LOADING_FALLBACK_S
    //   2. readiness + a short pause at 100% → start the fade
    //   3. fade complete → reveal the globe + HUD, drop the splash
    // A clock that stalls (a non-finite or zero delta from the XR frame
    // timestamp) must not stall the handover with it: advance by a
    // nominal frame instead, so the fallback and the fade still land.
    const handoverDelta = Number.isFinite(delta) && delta > 0 ? delta : 1 / 60
    loopElapsed += handoverDelta
    const splash = active.loading
    if (splash && !loadingDisposed) {
      if (!loadingFinalized && loopElapsed >= LOADING_FALLBACK_S) {
        logger.warn(
          `[VR] no texture readiness after ${LOADING_FALLBACK_S}s — dismissing the loading scene anyway`,
        )
        finishLoading()
      }
      if (
        loadingFinalized &&
        !fadeStarted &&
        loopElapsed - (loadingReadyAt ?? 0) >= LOADING_READY_PAUSE_S
      ) {
        fadeStarted = true
        void splash.fadeOut()
      }
      // Drive the loading scene's animation (rings spin, sphere pulses,
      // fade-out tween, progress bar ease).
      splash.update(handoverDelta)
      if (fadeStarted && splash.isFadedOut()) revealScene()
    }

    // Diagnostic readout: position every frame (it is camera-locked),
    // re-text it at 1 Hz — `getDatasets()` builds a fresh array each
    // call, which is why the browse poll is 1 Hz too.
    if (debugPanel) {
      debugPanel.update(active.camera)
      debugFrameCount++
      if (now - lastDebugRefreshMs > 1000) {
        // AYNI: frame rate over the last refresh, and each video's decode
        // health — what a phone test of a real-time overlay needs to see.
        const fps = (debugFrameCount * 1000) / Math.max(1, now - lastDebugRefreshMs)
        debugFrameCount = 0
        const baseSpec = ctx.getDatasetTexture()
        const baseVideo = baseSpec?.kind === 'video' ? baseSpec.element : null
        const rtVideo = ctx.getMapLayerImages?.()?.overlays.find(o => o.image instanceof HTMLVideoElement)?.image as HTMLVideoElement | undefined
        lastDebugRefreshMs = now
        debugPanel.setLines([
          `fps=${fps.toFixed(0)}/${(session as unknown as { frameRate?: number }).frameRate ?? '-'} base ${describeDecode(baseVideo)} | rt ${describeDecode(rtVideo ?? null)}`,
          `${isAr ? 'AR' : 'VR'} class=${sessionTelemetry.inputClass} src=${session.inputSources.length} ` +
            `pad=${hasGamepadInput() ? 'y' : 'n'} domOv=${domOverlayActive ? 'y' : 'n'}`,
          `hand=${handheldAr ? 'y' : 'n'} rotate=${rotateTouchMounted ? 'y' : 'n'} ` +
            `tl=${timeline.isVisible() ? 'y' : 'n'} ` +
            `zoomUi=${zoomOverlay !== null ? 'y' : 'n'}`,
          `img=${ctx.getDatasetTexture() ? 'y' : 'n'} load=${active.loading ? 'y' : 'n'} ` +
            `anchor=${globePlacement.anchor() ? 'y' : 'n'} place=${placement ? placement.getStep() : '-'}`,
          `cat=${ctx.getDatasets().length} panels=${ctx.getPanelCount()} ` +
            `scale=${active.scene.globe.scale.x.toFixed(2)}`,
          `loadT=${loopElapsed.toFixed(1)} ready=${loadingFinalized ? 'y' : 'n'} ` +
            `fade=${fadeStarted ? 'y' : 'n'} shown=${loadingDisposed ? 'y' : 'n'}`,
          // AYNI: the marker scan. `-` = not on this session; the side is
          // the marker's measured size, to hold a ruler against.
          `marker=${markerAlign ? markerAlign.getStatus() : '-'} ` +
            `side=${markerAlign?.getLastSize() ? `${(markerAlign.getLastSize()! * 100).toFixed(1)}cm` : '-'}`,
        ])
      }
    }

    // AYNI (Fully VR, owner 2026-11-03): last before drawing, once the globe's
    // turn for this frame is settled (inputs, spin, fly-to, a lead's), so the
    // stars never trail the globe. Centred on the viewer; the globe is lit by
    // the Sun at the sky's clock.
    if (nightSky && nightSkyRoot && satelliteLayer) {
      const time = satelliteLayer.engine.time
      const on = satelliteLayer.fullVrOn
      if (on) active.camera.getWorldPosition(nightSkyRoot.position)
      nightSky.update(on, on ? fullVrSkyTurn(time) : null, time, satelliteViewport.y)
      const coupled = fullVrCoupled()
      if (coupled) active.scene.setSunTime(time)
      else if (fullVrWas) active.scene.setSunTime(null)
      fullVrWas = coupled
    }

    active.renderer.render(active.scene.scene, active.camera)
  })

  // --- Teardown ---
  const onSessionEnd = (): void => {
    logger.info('[VR] Session ended, disposing resources')

    // Telemetry: emit `vr_session_ended` once per session, before
    // any disposal happens so frame counters / timestamps still
    // exist. mean_fps = total frames / wall-clock duration (a true
    // arithmetic mean, not a median — the name reflects what we
    // compute). 0 when the session was too short for a meaningful
    // sample; dashboards filter `mean_fps > 0` to exclude these.
    // For per-window medians, the perf_sampler emits
    // fps_median_10s during the session.
    if (sessionTelemetry.sessionStartedAtWall > 0) {
      const durationMs = Math.max(0, Date.now() - sessionTelemetry.sessionStartedAtWall)
      const meanFps = durationMs >= 1000
        ? Math.round((sessionTelemetry.frames * 1000) / durationMs)
        : 0
      emit({
        event_type: 'vr_session_ended',
        mode,
        exit_reason: sessionTelemetry.exitReason,
        duration_ms: durationMs,
        mean_fps: meanFps,
        // Snapshot of the loaded dataset at end-of-session. May
        // differ from `vr_session_started.layer_id` when the user
        // loaded something different while in VR.
        layer_id: ctx.getDatasetId() ?? '',
      })
    }

    if (!active) return
    const a = active
    active = null
    a.renderer.setAnimationLoop(null)
    // The probe cache is module-scoped (like its scratch canvas), so
    // clear it or the next session opens showing the last session's
    // number until the first sample lands.
    resetVrProbe()
    // Resolve any in-flight flyTo so awaiting callers don't hang
    // after the session ends (the chat's onFlyTo handler, a Khipu step).
    cancelFlyTo()
    a.interaction.dispose()
    a.disposeZoomOverlay()
    markerAlign?.dispose()
    if (markerOutline) {
      a.scene.scene.remove(markerOutline.mesh)
      markerOutline.dispose()
    }
    markerFrame = null
    if (debugPanel) {
      a.scene.scene.remove(debugPanel.mesh)
      debugPanel.dispose()
      debugPanel = null
    }
    // AYNI: the place the globe last faced the viewer with, so the browser
    // globe can turn to it (main.ts onSessionEnd), and the refresh's pieces.
    a.camera.getWorldPosition(scratchCamPos)
    const facing = scratchCamPos.clone().sub(a.scene.globe.position)
    let lastView: { lat: number; lon: number } | undefined
    if (facing.lengthSq() > 1e-6) {
      facing.normalize().applyQuaternion(a.scene.globe.quaternion.clone().invert())
      lastView = {
        lat: (Math.asin(Math.max(-1, Math.min(1, facing.y))) * 180) / Math.PI,
        lon: (Math.atan2(-facing.z, facing.x) * 180) / Math.PI,
      }
    }
    notifyActive = null
    pinMarks.dispose()
    focusZoomView.dispose()
    clearFocusZoom()
    a.scene.scene.remove(legendPanel.mesh)
    legendPanel.dispose()
    a.scene.scene.remove(readout.mesh)
    readout.dispose()
    a.hud.dispose()
    a.browse.dispose()
    a.scene.scene.remove(a.timeline.mesh)
    a.timeline.dispose()
    a.scene.scene.remove(a.timeLabel.mesh)
    a.timeLabel.dispose()
    a.geoMedia.dispose()
    for (const layers of a.vectorLayers.values()) layers.dispose()
    a.vectorLayers.clear()
    a.satellites?.dispose()
    a.satelliteSky?.dispose()
    nightSky?.dispose()
    if (nightSkyRoot) a.scene.scene.remove(nightSkyRoot)
    ctx.getSatellites?.()?.setSkyAvailable(false)
    a.scene.scene.remove(a.worldPanels.group)
    a.worldPanels.dispose()
    a.scene.scene.remove(a.khipu.mesh)
    a.khipu.dispose()
    // Loading scene may still be present if the user exited before
    // dataset finished loading. Dispose it explicitly so we don't
    // leak the canvases + textures. Flag handshake with the fade-out
    // path ensures we never double-dispose.
    if (a.loading && !loadingDisposed) {
      loadingDisposed = true
      a.scene.scene.remove(a.loading.group)
      a.loading.dispose()
    }
    if (a.placement) {
      a.scene.scene.remove(a.placement.reticleGroup)
      a.scene.scene.remove(a.placement.railGroup)
      a.scene.scene.remove(a.placement.placeButtonMesh)
      a.placement.dispose()
    }
    // Anchors are bound to the XR session; deleting is optional
    // (they're implicitly cleaned up when the session ends) but
    // explicit disposal avoids a brief "still tracked" state if
    // anything else held a reference. What is saved for the next
    // session stays saved.
    globePlacement.dispose()
    // Cancel the hit-test source so the platform releases the
    // viewer-space tracking subscription. The subscription
    // otherwise lives until session-end garbage-collects it
    // implicitly, which wastes work during the teardown window.
    if (a.hitTestSource) {
      try { a.hitTestSource.cancel() } catch { /* already cancelled */ }
    }
    a.scene.dispose()
    releaseRenderer(a.renderer)
    a.renderer.domElement.remove()
    ctx.onSessionEnd?.(lastView)
  }
  session.addEventListener('end', onSessionEnd)
  // AYNI (Quest review C1): the full teardown is listening now.
  session.removeEventListener('end', onEndWhileStarting)
  starting = null
  if (endedWhileStarting) {
    logger.info('[VR] Session ended while it was starting')
    onSessionEnd()
    return
  }

  logger.info(`[VR] ${isAr ? 'AR passthrough' : 'VR'} session started`)
}

/**
 * AYNI (Quest review C1): the session being built, from its request until
 * its full teardown is listening; null otherwise. `enterImmersive` ends
 * and releases it when the build throws.
 */
let starting: { session: XRSession; ended: () => boolean; release: () => void } | null = null

/**
 * Enter an immersive session. AYNI (Quest review C1): a build that throws
 * after the session was granted no longer leaves it running behind a black
 * view, or `active` set so that Enter is refused until a reload. The
 * session is ended and its renderer and canvas let go; a session the
 * person ended while it was starting (the Meta button) is not an error.
 */
export async function enterImmersive(mode: VrMode, ctx: VrSessionContext): Promise<void> {
  try {
    await buildImmersive(mode, ctx)
  } catch (err) {
    const s = starting
    starting = null
    if (!s) throw err
    const endedByPerson = s.ended()
    if (active?.session === s.session) {
      active.renderer.setAnimationLoop(null)
      active = null
    }
    notifyActive = null
    s.release()
    if (!endedByPerson) {
      await s.session.end().catch(() => { /* already gone */ })
      throw err
    }
    logger.info('[VR] Session ended while it was starting:', err)
    ctx.onSessionEnd?.()
  }
}

/** Convenience wrapper — request `immersive-vr` (full virtual environment). */
export const enterVr = (ctx: VrSessionContext): Promise<void> =>
  enterImmersive('vr', ctx)

/** Convenience wrapper — request `immersive-ar` (passthrough mixed reality). */
export const enterAr = (ctx: VrSessionContext): Promise<void> =>
  enterImmersive('ar', ctx)

/**
 * End the current VR session if one is active. Safe to call
 * unconditionally. Resource cleanup happens inside the 'end' handler
 * registered in `enterVr`, so this is just a thin wrapper.
 */
export async function exitVr(): Promise<void> {
  if (!active) return
  try {
    await active.session.end()
  } catch (err) {
    logger.warn('[VR] exitVr: session.end() failed:', err)
  }
}
